import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useStockControl } from "@/hooks/useStockControl";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateScoutingData } from "@/lib/scoutingDataInvalidation";
import { useTranslation } from "react-i18next";
import { PILL_BASE, PILL_COLORS } from "@/lib/statusPill";
import { validateTreatmentLocationStock, type MissingProductInfo } from "@/lib/treatmentLocationValidation";
import { desktopAutoFocusRef } from "@/lib/touchAutoFocus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CalendarIcon, FileText, MapPin, FlaskConical, User, Layers, Edit3 } from "lucide-react";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Info, Rows, Plus, Check } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanerosVisibles, useLocationScope } from "@/hooks/useLocationScope";
import { useToast } from "@/hooks/use-toast";
import { useBlocksActiveCycles, formatCycleLabel } from "@/hooks/useBlocksActiveCycles";
import { format } from "date-fns";
import { EmptySelectState } from "@/components/ui/empty-select-state";
import MultiGreenhouseSelector, { GreenhouseSelection } from "./MultiGreenhouseSelector";
import TreatmentProductsSection, { TreatmentProduct } from "./TreatmentProductsSection";
import { resolveProductAmount, resolveCaldoLiters, classifyUnit } from "@/lib/treatmentDosing";
import { useDoseConfirm } from "@/components/pests/useDoseConfirm";
import { completionErrorText, isStockBlockCode } from "@/lib/treatmentCompletion";
import { translateStockError } from "@/lib/stockErrorMessages";
import { usePestTranslation } from "@/hooks/usePestTranslation";
import { type WizardStep } from "./TaskFormWizard";
import { TaskFormSheetShell } from "./TaskFormSheetShell";
import { StepperNode } from "./WizardStepperHeader";
import { LocationSummaryBar } from "./LocationSummaryBar";
import { logDatabaseError } from "@/lib/supabaseErrorLogger";
import { cn } from "@/lib/utils";
import { isValid } from "date-fns";
import { formatStock as formatStockUtil } from "@/lib/formatStock";
import { buildAutoTaskTitle } from "@/lib/taskTitle";
import { useTaskApplicationChoice } from "@/components/applicationContext/useTaskApplicationChoice";
import { PestSuggestionChips } from "@/components/pests/PestSuggestionChips";
import { hasPest, recipePestIds, suggestTaskPests } from "@/lib/pests/pestSuggestion";
import { useScoutingPestIds } from "@/hooks/useTaskPestSuggestions";
import { useRegistroMapaOff } from "@/hooks/useRegistroMapaOff";
import { useRegistryCatalog } from "@/hooks/useRegistryCatalog";
import { buildRegistryIndex } from "@/lib/registry/resolveRegistration";
import { validateRegistration, type RegistrationCheck } from "@/lib/registry/validateRegistration";
import { RegistrationCheckNote } from "@/components/inventory/RegistrationCheckNote";
import { applicationChecks } from "@/lib/applicationChecks";
import { pickEquipment } from "@/lib/machinery/equipmentRules";
import { pickApplicator } from "@/lib/applicators/applicatorRules";
import { ApplicationChecksNote } from "@/components/applicationContext/ApplicationChecksNote";

function defaultMacroTitle(id: string): string {
  switch (id) {
    case "details": return "Detalles";
    case "location": return "Ubicación";
    case "treatment": return "Tratamiento";
    case "schedule": return "Programación";
    default: return "";
  }
}
function defaultMacroSubtitle(id: string): string {
  switch (id) {
    case "details": return "Título, tipo, prioridad y responsable.";
    case "location": return "Elige invernaderos y secciones a tratar.";
    case "treatment": return "Plaga, producto y dosis estimada.";
    case "schedule": return "Cuándo se realiza, y notas finales.";
    default: return "";
  }
}
function SummaryRow({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="shrink-0">{icon}</span>
      <span className="text-sm text-foreground truncate">{text}</span>
    </div>
  );
}

interface Location {
  id: string;
  name: string;
}

interface Greenhouse {
  id: string;
  name: string;
  location_id: string | null;
}

interface Block {
  id: string;
  name: string;
  greenhouse_id: string;
  area_m2: number | null;
  row_start?: number | null;
  row_end?: number | null;
  /** Cuántas líneas tiene el sector, aunque no se sepa su numeración. */
  lines_count?: number | null;
}

/**
 * Hasta dónde se pueden pedir líneas en un sector. Tres casos reales en producción:
 *  - row_start+row_end puestos (113 de 201 sectores) → ese rango exacto.
 *  - solo lines_count (71) → se sabe CUÁNTAS hay pero no su numeración: se asume 1..N,
 *    que es mejor que no validar nada (era lo que dejaba escribir "567 – 7890").
 *  - ninguno (17) → no hay información: no se pueden pedir líneas.
 * BlockRowSetupDialog escribe los tres campos; BlockFormDialog permite solo el contador,
 * de ahí que existan sectores con contador pero sin rango.
 */
function getLineRange(block: Block): { min: number; max: number; derived: boolean } | null {
  if (block.row_start != null && block.row_end != null) {
    return { min: block.row_start, max: block.row_end, derived: false };
  }
  if (block.lines_count != null && block.lines_count > 0) {
    return { min: 1, max: block.lines_count, derived: true };
  }
  return null;
}

interface Pest {
  id: string;
  name: string;
}

interface Treatment {
  id: string;
  name: string;
  mode: string | null;
}

interface InventoryProduct {
  id: string;
  product_name: string;
  unit: string;
  quantity: number;
  units_per_package?: number | null;
  /** Para el aviso del nº de registro al elegir el producto (F4). */
  registration_number?: string | null;
  category?: string | null;
}

interface CompanyWorker {
  user_id: string;
  full_name: string;
  role: string;
}

interface TaskFormData {
  title: string;
  type: string;
  priority: string;
  greenhouse_selections: GreenhouseSelection[];
  pest_id: string;
  treatment_id: string;
  scheduled_date: string;
  notes: string;
  assigned_to: string;
}

// Pre-filled data interface for scouting/voice integration
export interface PrefilledTaskData {
  scouting_session_id?: string;
  location_id?: string | null;
  greenhouse_id?: string | null;
  block_id?: string | null;
  pest_id?: string | null;
  pest_name?: string | null;
  scheduled_date?: string;
  title?: string;
  type?: string;
  priority?: string;
  treatment_id?: string;
  assigned_to?: string;
  observed_rows?: Record<string, { row_start: number | null; row_end: number | null }>;
  // When true, locks location, greenhouse, block, and pest fields
  fromScouting?: boolean;
  // Display-only chain for the wizard header (Finca · Nave · Bloque · Línea X)
  location_chain?: string | null;
}

interface TaskFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task?: any;
  onSuccess: () => void;
  defaultAssignedTo?: string;
  prefilledData?: PrefilledTaskData | null;
}

// Resolve a product's editable rate into its absolute amount (single source of truth).
function resolveProductRate(p: TreatmentProduct, areaM2: number, caldoL: number | null): TreatmentProduct {
  const r = resolveProductAmount({
    value: p.rate_value,
    unit: p.rate_unit,
    areaM2,
    caldoL,
    stockUnit: p.stock_unit || p.unit,
  });
  const basis = classifyUnit(p.rate_unit);
  return {
    ...p,
    amount: r.error ? 0 : r.amount,
    unit: r.amountUnit,
    dose_value: basis === "per_caldo" ? p.rate_value : null,
    dose_unit: basis === "per_caldo" ? p.rate_unit : null,
    amount_per_ha: basis === "per_area" ? p.rate_value : null,
  };
}

// Normalize stored custom_products (from an existing task) into editable products.
// Legacy entries (no rate_unit) keep their absolute amount as-is — never re-multiplied.
function normalizeLoadedProducts(
  cps: any[],
  inv: InventoryProduct[],
  areaM2: number,
  caldoL: number | null
): TreatmentProduct[] {
  return (cps || [])
    .filter((cp) => cp && cp.product_id)
    .map((cp) => {
      const invItem = inv.find((i) => i.id === cp.product_id);
      const stock_unit = cp.stock_unit || invItem?.unit || cp.unit || "L";
      const rate_unit = cp.rate_unit || cp.unit || stock_unit;
      const rate_value = cp.rate_value != null ? cp.rate_value : cp.amount != null ? cp.amount : 1;
      const base: TreatmentProduct = {
        product_id: cp.product_id,
        product_name: cp.product_name || invItem?.product_name || "",
        amount: cp.amount != null ? cp.amount : 0,
        unit: cp.unit || stock_unit,
        stock: invItem?.quantity ?? 0,
        is_from_treatment: !!cp.is_from_treatment,
        rate_value,
        rate_unit,
        stock_unit,
        units_per_package: cp.units_per_package ?? invItem?.units_per_package ?? null,
      };
      return resolveProductRate(base, areaM2, caldoL);
    });
}

// Helpers for splitting/joining scheduled_date
function parseScheduledDateTime(s: string): { date: Date | undefined; time: string } {
  if (!s) return { date: undefined, time: "08:00" };
  const [datePart, timePart] = s.split("T");
  const d = datePart ? new Date(datePart + "T00:00:00") : undefined;
  return { date: d && isValid(d) ? d : undefined, time: timePart || "08:00" };
}

function setScheduledDateTime(date: Date, time: string): string {
  return format(date, "yyyy-MM-dd") + "T" + (time || "08:00");
}

export default function TaskFormDialog({ open, onOpenChange, task, onSuccess, defaultAssignedTo, prefilledData }: TaskFormDialogProps) {
  const { t } = useTranslation();
  const { getName: getPestName } = usePestTranslation();
  const { company, user, role } = useAuth();
  const { flexible: stockFlexible } = useStockControl();
  const { allowedLocationIds } = useLocationScope();
  const { esVisible } = useCompanerosVisibles();
  const { toast } = useToast();
  const { confirmDoses, doseConfirmDialog } = useDoseConfirm();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [wizardDatePopoverOpen, setWizardDatePopoverOpen] = useState(false);
  const [showMissingFieldsWarning, setShowMissingFieldsWarning] = useState(false);
  const [missingLocation, setMissingLocation] = useState(false);
  const [missingWorker, setMissingWorker] = useState(false);

  // Detect if editing a scouting-linked task (locks certain fields)
  const isScoutingLinked = !!(task?.scouting_session_id);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [greenhouses, setGreenhouses] = useState<Greenhouse[]>([]);
  const [blocksByGreenhouse, setBlocksByGreenhouse] = useState<Map<string, Block[]>>(new Map());
  const [pests, setPests] = useState<Pest[]>([]);
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [companyWorkers, setCompanyWorkers] = useState<CompanyWorker[]>([]);
  
  // Products from treatment or manual
  const [treatmentProducts, setTreatmentProducts] = useState<TreatmentProduct[]>([]);
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [inventoryProducts, setInventoryProducts] = useState<InventoryProduct[]>([]);
  const [locationStockProductIds, setLocationStockProductIds] = useState<Set<string> | null>(null); // null = not filtered
  
  // Water volume (entry-level, stored in custom_products[0])
  const [waterVolume, setWaterVolume] = useState<number | null>(null);
  const [waterVolumeUnit, setWaterVolumeUnit] = useState<string>("L");

  // Observed rows (optional, informational)
  const [observedRows, setObservedRows] = useState<Record<string, { row_start: string; row_end: string }>>({});
  const [showRowInputs, setShowRowInputs] = useState(false);
  const [rowErrors, setRowErrors] = useState<Record<string, { start?: string; end?: string }>>({});
  
  // Pest-treatment associations
  const [pestTreatmentMap, setPestTreatmentMap] = useState<Map<string, Set<string>>>(new Map());
  
  const [irmWarnings, setIrmWarnings] = useState<Array<{
    product_name: string;
    usage_count: number;
    limit: number;
    will_exceed: boolean;
  }>>([]);
  const [irmLoading, setIrmLoading] = useState(false);
  const [irmError, setIrmError] = useState<string | null>(null);

  // Location stock warning for treatment products
  const [showLocationStockWarning, setShowLocationStockWarning] = useState(false);
  const [locationStockWarningProducts, setLocationStockWarningProducts] = useState<MissingProductInfo[]>([]);
  
  // Estado del asistente
  const [wizardStep, setWizardStep] = useState(0);
  // New: macro-step index for the bottom-sheet wizard (Detalles/Ubicación/Tratamiento/Programación)
  const [macroIndex, setMacroIndex] = useState(0);
  const [maxMacroReached, setMaxMacroReached] = useState(0);
  // Track if user manually chose a worker (prevent auto-overwrite)
  const [manuallyAssigned, setManuallyAssigned] = useState(false);
  // Asignados (block_worker_assignments) a los sectores elegidos: alimenta el
  // grupo "Asignados a esta ubicación" del selector de trabajador.
  const [blockAssignedUserIds, setBlockAssignedUserIds] = useState<Set<string>>(new Set());
  // El grupo "Otros" del selector va plegado: lo rápido es tocar a un asignado.
  // Notas plegadas tras un «+»: casi nunca se usan y la caja siempre abierta estorbaba.
  const [notesOpen, setNotesOpen] = useState(false);
  /* El «⋯» de la cabecera: «Maquinaria…» (F2, 26-09-2026). Solo admin y
     encargado; ver la guarda en `useTaskApplicationChoice`. */
  const appChoice = useTaskApplicationChoice({ open, companyId: company?.id, role, task });

  const [formData, setFormData] = useState<TaskFormData>({
    title: "",
    type: "treatment",
    // Sin prioridad preseleccionada en una tarea nueva: el usuario elige uno de
    // los tres botones de color. Al guardar cae a "medium" si se dejó en blanco.
    priority: "",
    greenhouse_selections: [],
    pest_id: "",
    treatment_id: "",
    scheduled_date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    notes: "",
    assigned_to: ""
  });

   // Reset wizard step when dialog opens/closes
  useEffect(() => {
    if (open) {
      setManuallyAssigned(false);
      setMacroIndex(0);
      setMaxMacroReached(0);
      // Notas plegadas al abrir; editando una tarea que ya las tiene, abiertas.
      setNotesOpen(false);
      // When opening from scouting, skip hidden title step and start at first visible step
      if (prefilledData?.fromScouting) {
        // Title step (index 0) is hidden, so start at index 1 (type-priority)
        setWizardStep(1);
      } else {
        setWizardStep(0);
      }
    }
  }, [open, prefilledData?.fromScouting]);

  // Determines whether the dialog has any user-entered input. When false,
  // clicking the X closes immediately without the "discard changes" confirm.
  const taskFormIsDirty = task
    ? undefined // Edit mode: rely on auto-detection from native input events
    : (
        wizardStep > 0 ||
        manuallyAssigned ||
        formData.title.trim() !== "" ||
        formData.greenhouse_selections.length > 0 ||
        (formData.pest_id !== "" && formData.pest_id !== "none") ||
        (formData.treatment_id !== "" && formData.treatment_id !== "none") ||
        (formData.notes ?? "").trim() !== "" ||
        formData.assigned_to !== ""
      );

  // Gather all block IDs for cycle lookup
  const allBlockIds = Array.from(blocksByGreenhouse.values()).flatMap(blocks => blocks.map(b => b.id));
  
  // Fetch active cycles for loaded blocks
  const { cyclesMap: blockCyclesMap } = useBlocksActiveCycles(
    allBlockIds,
    company?.id,
    formData.scheduled_date?.split("T")[0]
  );

  useEffect(() => {
    if (open && company?.id) {
      fetchLocations();
      fetchGreenhouses();
      fetchPests();
      fetchTreatments();
      fetchPestTreatmentAssociations();
      fetchInventoryProducts();
      fetchCompanyWorkers();
    }
    if (!open) {
      setSelectedLocation("");
    }
  }, [open, company?.id]);

  // Fetch blocks when greenhouses change
  useEffect(() => {
    if (greenhouses.length > 0 && company?.id) {
      fetchAllBlocks();
    }
  }, [greenhouses, company?.id]);

  // Stable keys for dependency tracking
  const treatmentProductIdsKey = useMemo(() => treatmentProducts.map(p => p.product_id).sort().join(','), [treatmentProducts]);
  const greenhouseSelectionsKey = useMemo(() => formData.greenhouse_selections.map(s => `${s.greenhouse_id}:${s.block_ids.join('|')}`).join(','), [formData.greenhouse_selections]);

  /**
   * La Ubicación de verdad, para comprobar el stock.
   *
   * El desplegable de Ubicación es un FILTRO, no la respuesta: puede quedarse en
   * «Todas las ubicaciones» y aun así saberse perfectamente dónde es, porque cada
   * invernadero pertenece a una sola ubicación. Antes se miraba solo el
   * desplegable y por eso saltaba «selecciona una localización» a quien ya había
   * elegido nave y sectores.
   *
   * null solo cuando de verdad no se puede saber: sin nada elegido, o con naves de
   * dos ubicaciones distintas (ahí no hay UNA sola donde mirar el stock).
   */
  const effectiveLocationId = useMemo<string | null>(() => {
    if (selectedLocation && selectedLocation !== "all") return selectedLocation;
    const ids = new Set(
      formData.greenhouse_selections
        .map(s => greenhouses.find(g => g.id === s.greenhouse_id)?.location_id)
        .filter((id): id is string => !!id),
    );
    return ids.size === 1 ? [...ids][0] : null;
  }, [selectedLocation, formData.greenhouse_selections, greenhouses]);

  // Check IRM limits when selections and treatment change
  useEffect(() => {
    const firstBlockId = formData.greenhouse_selections[0]?.block_ids[0];
    if (firstBlockId && formData.treatment_id && formData.treatment_id !== "none") {
      checkIrmLimits(firstBlockId);
    } else {
      setIrmWarnings([]);
      setIrmError(null);
      setIrmLoading(false);
    }
  }, [greenhouseSelectionsKey, formData.treatment_id, treatmentProductIdsKey]);

  // Fetch treatment products when treatment changes
  useEffect(() => {
    if (formData.treatment_id && formData.treatment_id !== "none" && company?.id) {
      fetchTreatmentProducts(formData.treatment_id);
    } else if (formData.treatment_id === "none") {
      // Manual products mode - show empty products section for customization
      if (!isCustomizing) {
        setIsCustomizing(true);
      }
    } else {
      setTreatmentProducts([]);
      setIsCustomizing(false);
    }
  }, [formData.treatment_id, company?.id]);

  // Initialize form data
  useEffect(() => {
    if (task) {
      // Editing existing task
      const selections: GreenhouseSelection[] = [];
      if (task.greenhouse_id) {
        const gh = greenhouses.find(g => g.id === task.greenhouse_id);
        selections.push({
          greenhouse_id: task.greenhouse_id,
          greenhouse_name: gh?.name || "",
        block_ids: (() => {
          const targetBlocks = task.custom_products?.[0]?.target_blocks;
          return Array.isArray(targetBlocks) && targetBlocks.length > 0
            ? targetBlocks
            : (task.block_id ? [task.block_id] : []);
        })(),
        });
        if (gh?.location_id) {
          setSelectedLocation(gh.location_id);
        }
      }
      
      setFormData({
        title: task.title || "",
        type: task.type || "treatment",
        priority: task.priority || "medium",
        greenhouse_selections: selections,
        pest_id: task.pest_id || "",
        treatment_id: task.treatment_id || "",
        scheduled_date: task.scheduled_date ? format(new Date(task.scheduled_date), "yyyy-MM-dd'T'HH:mm") : format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        notes: task.notes || "",
        assigned_to: task.assigned_to || ""
      });
      // Preserve the task's existing worker on edit — treat it as a manual choice so the
      // block-based auto-detect effect doesn't silently blank it.
      if (task.assigned_to) setManuallyAssigned(true);

      if (task.custom_products && Array.isArray(task.custom_products)) {
        // Load water volume from first product entry first, so we can resolve amounts.
        const firstEntry = task.custom_products[0];
        const loadedCaldo = firstEntry?.water_volume != null ? firstEntry.water_volume : null;
        const loadedCaldoUnit = firstEntry?.water_volume_unit || "L";
        if (loadedCaldo != null) {
          setWaterVolume(loadedCaldo);
          setWaterVolumeUnit(loadedCaldoUnit);
        } else {
          setWaterVolume(null);
          setWaterVolumeUnit("L");
        }
        setTreatmentProducts(normalizeLoadedProducts(task.custom_products, inventoryProducts, 0, loadedCaldo));
        setIsCustomizing(true);
        // Load observed rows from first product entry
        const obsRows = firstEntry?.observed_rows;
        if (obsRows && typeof obsRows === 'object') {
          const rows: Record<string, { row_start: string; row_end: string }> = {};
          for (const [blockId, data] of Object.entries(obsRows as Record<string, { row_start: number | null; row_end: number | null }>)) {
            rows[blockId] = { row_start: data.row_start?.toString() || "", row_end: data.row_end?.toString() || "" };
          }
          setObservedRows(rows);
          setShowRowInputs(Object.keys(rows).length > 0);
        } else {
          setObservedRows({});
          setShowRowInputs(false);
        }
      } else {
        setWaterVolume(null);
        setWaterVolumeUnit("L");
        setObservedRows({});
        setShowRowInputs(false);
      }
    } else if (prefilledData) {
      // Pre-fill from scouting session or voice command
      const selections: GreenhouseSelection[] = [];
      if (prefilledData.greenhouse_id) {
        const gh = greenhouses.find(g => g.id === prefilledData.greenhouse_id);
        selections.push({
          greenhouse_id: prefilledData.greenhouse_id,
          greenhouse_name: gh?.name || "",
          block_ids: prefilledData.block_id ? [prefilledData.block_id] : [],
        });
        const locId = prefilledData.location_id || gh?.location_id;
        if (locId) {
          setSelectedLocation(locId);
        }
      } else if (prefilledData.location_id) {
        setSelectedLocation(prefilledData.location_id);
      }
      
      setFormData({
        title: prefilledData.title || "",
        type: prefilledData.type || "treatment",
        priority: prefilledData.priority || "high",
        greenhouse_selections: selections,
        pest_id: prefilledData.pest_id || "",
        treatment_id: prefilledData.treatment_id || "",
        scheduled_date: prefilledData.scheduled_date || format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        notes: "",
        assigned_to: prefilledData.assigned_to || defaultAssignedTo || ""
      });
      // La preselección (voz o "Asignar tarea" desde Equipo) cuenta como elección
      // manual: sin esto, el efecto de auto-detección la borraba al abrir.
      if (prefilledData.assigned_to || defaultAssignedTo) setManuallyAssigned(true);

      setTreatmentProducts([]);
      setIsCustomizing(false);
      
      // Initialize observed rows from scouting data
      if (prefilledData.observed_rows) {
        const rows: Record<string, { row_start: string; row_end: string }> = {};
        for (const [blockId, data] of Object.entries(prefilledData.observed_rows)) {
          rows[blockId] = {
            row_start: data.row_start?.toString() || "",
            row_end: data.row_end?.toString() || "",
          };
        }
        setObservedRows(rows);
        setShowRowInputs(Object.keys(rows).length > 0);
      } else {
        setObservedRows({});
        setShowRowInputs(false);
      }
    } else {
      // New task
      setFormData({
        title: "",
        type: "treatment",
        priority: "",
        greenhouse_selections: [],
        pest_id: "",
        treatment_id: "",
        scheduled_date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        notes: "",
        assigned_to: defaultAssignedTo || ""
      });
      if (defaultAssignedTo) setManuallyAssigned(true);
      setTreatmentProducts([]);
      setIsCustomizing(false);
      setWaterVolume(null);
      setWaterVolumeUnit("L");
      setObservedRows({});
      setShowRowInputs(false);
    }
  }, [task, open, defaultAssignedTo, prefilledData, greenhouses]);

  // Calculate total area in hectares from selected blocks
  const calculateTotalAreaHa = useCallback((): number => {
    let totalM2 = 0;
    for (const sel of formData.greenhouse_selections) {
      const blocks = blocksByGreenhouse.get(sel.greenhouse_id) || [];
      for (const blockId of sel.block_ids) {
        const block = blocks.find(b => b.id === blockId);
        if (block?.area_m2 && block.area_m2 > 0) {
          totalM2 += block.area_m2;
        }
      }
    }
    return totalM2 / 10000;
  }, [formData.greenhouse_selections, blocksByGreenhouse]);

  const totalAreaHa = useMemo(() => calculateTotalAreaHa(), [calculateTotalAreaHa]);
  const totalAreaM2 = useMemo(() => totalAreaHa * 10000, [totalAreaHa]);
  const caldoL = useMemo(
    () => resolveCaldoLiters(waterVolume, waterVolumeUnit, totalAreaM2),
    [waterVolume, waterVolumeUnit, totalAreaM2]
  );
  // True when a product is dosed per caldo but the caldo volume is not set yet.
  const caldoMissing = useMemo(
    () =>
      treatmentProducts.some((p) => classifyUnit(p.rate_unit) === "per_caldo") &&
      !(caldoL != null && caldoL > 0),
    [treatmentProducts, caldoL]
  );

  // Recompute every product's absolute amount whenever area or caldo changes.
  useEffect(() => {
    setTreatmentProducts((prev) => {
      if (prev.length === 0) return prev;
      const next = prev.map((p) => resolveProductRate(p, totalAreaM2, caldoL));
      const changed = next.some((p, i) => p.amount !== prev[i].amount || p.unit !== prev[i].unit);
      return changed ? next : prev;
    });
  }, [totalAreaM2, caldoL]);

  // Keep stock in sync when inventory loads (custom_products don't store stock).
  useEffect(() => {
    if (inventoryProducts.length === 0) return;
    setTreatmentProducts((prev) => {
      if (prev.length === 0) return prev;
      let changed = false;
      const next = prev.map((p) => {
        const inv = inventoryProducts.find((i) => i.id === p.product_id);
        /* MANDA LA UNIDAD VIVA DEL PRODUCTO, no la copia guardada en la tarea.
           Esto es lo que dejaba tareas imposibles: las creadas antes de que
           existiera `stock_unit` la traen a null, y todo lo que hay detrás hace
           `p.stock_unit || p.unit`, o sea, se cae a la unidad de la DOSIS y da
           por hecho que el producto se lleva en esa misma unidad. Con «13,13 L»
           de un producto que está en kilos, las dos coincidían trivialmente y
           nadie se enteraba hasta el final, al completar.
           La copia sirve para el histórico —lo que era verdad aquel día— pero
           para decidir CUÁNTO se descuenta ahora la verdad es el producto. */
        if (inv && (inv.quantity !== p.stock || (inv.unit && inv.unit !== p.stock_unit))) {
          changed = true;
          return { ...p, stock: inv.quantity, stock_unit: inv.unit || p.stock_unit };
        }
        return p;
      });
      return changed ? next : prev;
    });
  }, [inventoryProducts]);

  const fetchTreatmentProducts = async (treatmentId: string) => {
    if (!company?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('treatment_components')
        .select(`*, inventory!inner(product_name, quantity, unit)`)
        .eq('treatment_id', treatmentId)
        .eq('company_id', company.id);

      if (error) {
        console.error('Error fetching treatment components:', error);
        return;
      }

      const areaM2 = calculateTotalAreaHa() * 10000;

      const products: TreatmentProduct[] = (data || []).map((c: any) => {
        const componentUnit = c.unit || c.inventory?.unit || 'L';
        const stockUnit = c.inventory?.unit || componentUnit;
        const isPerHa = c.amount_is_per_ha !== false; // default true
        // Prefer the explicit recipe rate (incl. caldo); fall back to legacy per-ha/fixed.
        const rate_unit = c.rate_unit || (isPerHa ? `${componentUnit}/ha` : componentUnit);
        const rate_value = c.rate_value != null ? c.rate_value : (c.amount || 1);

        const base: TreatmentProduct = {
          product_id: c.product_id,
          product_name: c.inventory?.product_name || 'Unknown',
          amount: 0,
          unit: componentUnit,
          stock: c.inventory?.quantity || 0,
          is_from_treatment: true,
          rate_value,
          rate_unit,
          stock_unit: stockUnit,
        };
        return resolveProductRate(base, areaM2, caldoL);
      });

      setTreatmentProducts(products);
      setIsCustomizing(false);

      // Pre-fill the caldo from the recipe's default (resolves per-caldo products).
      try {
        const { data: trow } = await supabase
          .from('treatments')
          .select('default_caldo_value, default_caldo_unit')
          .eq('id', treatmentId)
          .maybeSingle();
        if ((trow as any)?.default_caldo_value != null) {
          setWaterVolume((trow as any).default_caldo_value);
          setWaterVolumeUnit((trow as any).default_caldo_unit || 'L');
        }
      } catch (e) {
        // default caldo column may not exist yet — ignore, caldo stays manual
      }

      // --- Location stock validation (uses required quantities) ---
      if (effectiveLocationId && products.length > 0 && company?.id) {
        try {
          const requirements = products.map(p => ({ product_id: p.product_id, required: p.amount || 0 }));
          const result = await validateTreatmentLocationStock(company.id, treatmentId, effectiveLocationId, requirements);
          if (!result.valid) {
            setLocationStockWarningProducts(result.missingProducts);
            setShowLocationStockWarning(true);
          }
        } catch (err) {
          console.error('Error checking location stock for treatment:', err);
        }
      }
    } catch (err) {
      console.error('Error fetching treatment products:', err);
    }
  };

  const fetchInventoryProducts = async () => {
    if (!company?.id) return;
    
    const { data } = await supabase
      .from('inventory')
      .select('id, product_name, unit, quantity, units_per_package, registration_number, category')
      .eq('company_id', company.id)
      .order('product_name');
    
    setInventoryProducts((data || []) as InventoryProduct[]);
  };

  // Fetch location stock when selectedLocation changes to filter products
  const prevLocationRef = useRef<string>("");
  useEffect(() => {
    if (prevLocationRef.current === selectedLocation) return;
    prevLocationRef.current = selectedLocation;

    if (!selectedLocation || !company?.id) {
      setLocationStockProductIds(null);
      return;
    }

    const fetchLocationStock = async () => {
      // Get product IDs that have stock at this location
      const { data: stockRows } = await supabase
        .from('inventory_location_stock')
        .select('product_id')
        .eq('location_id', selectedLocation)
        .eq('company_id', company.id)
        .gt('quantity', 0);

      // Get product IDs that have ANY location assignment (to identify legacy/unassigned products)
      const { data: allAssigned } = await supabase
        .from('inventory_location_stock')
        .select('product_id')
        .eq('company_id', company.id);

      const assignedSet = new Set((allAssigned || []).map(r => r.product_id));
      const locationSet = new Set((stockRows || []).map(r => r.product_id));

      // Include products at this location + unassigned (legacy) products
      const allProducts = inventoryProducts.map(p => p.id);
      const filteredIds = new Set<string>();
      allProducts.forEach(id => {
        if (locationSet.has(id) || !assignedSet.has(id)) {
          filteredIds.add(id);
        }
      });

      setLocationStockProductIds(filteredIds);
    };

    fetchLocationStock();
  }, [selectedLocation, company?.id, inventoryProducts]);

  // Re-validate treatment stock when location or locationStockProductIds changes
  useEffect(() => {
    if (!effectiveLocationId || !formData.treatment_id || formData.treatment_id === "none") return;
    if (treatmentProducts.length === 0 || !company?.id) return;

    const revalidate = async () => {
      try {
        const requirements = treatmentProducts.map(p => ({ product_id: p.product_id, required: p.amount || 0 }));
        const result = await validateTreatmentLocationStock(company.id, formData.treatment_id, effectiveLocationId, requirements);
        if (!result.valid) {
          setLocationStockWarningProducts(result.missingProducts);
          setShowLocationStockWarning(true);
        }
      } catch (err) {
        console.error('Error re-validating treatment location stock:', err);
      }
    };
    revalidate();
  }, [locationStockProductIds, effectiveLocationId, formData.treatment_id, treatmentProductIdsKey]);

  // Asignados a los sectores elegidos: SIEMPRE se cargan (alimentan el grupo
  // "Asignados a esta ubicación"), y si el usuario no ha elegido a mano, con
  // exactamente 1 candidato visible se preselecciona.
  useEffect(() => {
    const allBlockIds = formData.greenhouse_selections.flatMap(s => s.block_ids);
    if (!company?.id) return;

    if (allBlockIds.length === 0) {
      setBlockAssignedUserIds(new Set());
      if (!manuallyAssigned) setFormData(prev => ({ ...prev, assigned_to: "" }));
      return;
    }

    const detectWorker = async () => {
      try {
        const { data } = await supabase
          .from("block_worker_assignments")
          .select("user_id")
          .eq("company_id", company.id)
          .eq("is_active", true)
          .in("block_id", allBlockIds);

        const ids = new Set((data || []).map(d => d.user_id));
        setBlockAssignedUserIds(ids);
        if (manuallyAssigned) return;

        // Solo se auto-asigna a alguien que exista en el desplegable: antes se
        // podía auto-asignar a un usuario invisible (p. ej. un admin) y el
        // selector quedaba en blanco con valor puesto.
        const visible = [...ids].filter(id => companyWorkers.some(w => w.user_id === id));
        if (visible.length === 1) {
          setFormData(prev => ({ ...prev, assigned_to: visible[0] }));
        } else {
          // 0 o varios candidatos — se limpia para forzar una elección explícita
          setFormData(prev => ({ ...prev, assigned_to: "" }));
        }
      } catch (err) {
        console.error("Error detecting assigned worker:", err);
      }
    };
    detectWorker();
  }, [greenhouseSelectionsKey, company?.id, manuallyAssigned, companyWorkers]);

  // Filtered inventory products based on location
  const filteredInventoryProducts = useMemo(() => {
    if (!locationStockProductIds) return inventoryProducts;
    return inventoryProducts.filter(p => locationStockProductIds.has(p.id));
  }, [inventoryProducts, locationStockProductIds]);

  const fetchPestTreatmentAssociations = async () => {
    if (!company?.id) return;
    
    try {
      const { data: companyTreatments } = await supabase
        .from("treatments")
        .select("id")
        .eq("company_id", company.id);
      
      const companyTreatmentIds = new Set((companyTreatments || []).map(t => t.id));

      const { data: mappings } = await supabase
        .from("treatment_pest_map")
        .select("pest_id, treatment_id");

      const map = new Map<string, Set<string>>();
      (mappings || []).forEach(m => {
        if (!companyTreatmentIds.has(m.treatment_id)) return;
        
        if (!map.has(m.pest_id)) {
          map.set(m.pest_id, new Set());
        }
        map.get(m.pest_id)!.add(m.treatment_id);
      });
      setPestTreatmentMap(map);
    } catch (err) {
      console.error("Error fetching pest-treatment associations:", err);
    }
  };

  /* SOLO SUS FINCAS EN EL SELECTOR (01-09-2026).
     Un encargado acotado a Las Norias podía CREAR una tarea en Campohermoso:
     el desplegable las listaba todas. Recortar aquí es lo que convierte el
     recorte de lectura en un recorte de verdad — de poco sirve esconderle las
     tareas de otra finca si puede fabricar una allí. */
  const fetchLocations = async () => {
    if (!company?.id) return;
    const { data } = await supabase
      .from("locations")
      .select("id, name")
      .eq("company_id", company.id)
      .order("name");
    const todas = data || [];
    setLocations(allowedLocationIds ? todas.filter((l) => allowedLocationIds.has(l.id)) : todas);
  };

  const fetchGreenhouses = async () => {
    if (!company?.id) return;
    const { data } = await supabase
      .from("greenhouses")
      .select("id, name, location_id")
      .eq("company_id", company.id);
    const todas = data || [];
    setGreenhouses(
      allowedLocationIds ? todas.filter((g) => g.location_id && allowedLocationIds.has(g.location_id)) : todas,
    );
  };

  const fetchAllBlocks = async () => {
    if (!company?.id) return;
    const { data } = await supabase
      .from("blocks")
      .select("id, name, greenhouse_id, area_m2, row_start, row_end, lines_count")
      .eq("company_id", company.id)
      .is("archived_at", null);
    
    // Group by greenhouse
    const byGreenhouse = new Map<string, Block[]>();
    (data || []).forEach(block => {
      if (!byGreenhouse.has(block.greenhouse_id)) {
        byGreenhouse.set(block.greenhouse_id, []);
      }
      byGreenhouse.get(block.greenhouse_id)!.push(block);
    });
    setBlocksByGreenhouse(byGreenhouse);
  };

  const fetchPests = async () => {
    if (!company?.id) return;
    const { data } = await supabase
      .from("pests")
      .select("id, name, translation_key")
      .or(`company_id.eq.${company.id},company_id.is.null`)
      .order("name");
    setPests(data || []);
  };

  const fetchTreatments = async () => {
    if (!company?.id) return;
    const { data } = await supabase
      .from("treatments")
      .select("id, name, mode")
      .eq("company_id", company.id)
      .order("name");
    setTreatments(data || []);
  };

  const fetchCompanyWorkers = async () => {
    if (!company?.id) return;
    
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, full_name')
        .eq('company_id', company.id)
        .order('full_name');
      
      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        return;
      }

      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .eq('company_id', company.id);
      
      if (rolesError) {
        console.error('Error fetching roles:', rolesError);
        return;
      }

      const roleMap = new Map<string, string>();
      (roles || []).forEach(r => roleMap.set(r.user_id, r.role));

      const workers: CompanyWorker[] = (profiles || [])
        .filter(p => {
          const role = roleMap.get(p.user_id);
          return role === 'worker' || role === 'manager' || role === 'perito';
        })
        .map(p => ({
          user_id: p.user_id,
          full_name: p.full_name || 'Sin nombre',
          role: roleMap.get(p.user_id) || 'worker'
        }));

      setCompanyWorkers(workers);
    } catch (err) {
      console.error('Error fetching company workers:', err);
    }
  };

  const checkIrmLimits = async (blockId: string) => {
    if (!blockId || !formData.treatment_id || formData.treatment_id === "none") return;
    
    setIrmLoading(true);
    setIrmError(null);
    
    try {
      const { data, error } = await supabase.rpc('check_pesticide_limit', {
        target_block_id: blockId,
        target_treatment_id: formData.treatment_id
      });

      if (error) {
        console.error('IRM check error:', error);
        setIrmError(error.message);
        return;
      }

      if (data && typeof data === 'object' && 'products' in data) {
        const result = data as { products: Array<{
          product_id: string;
          product_name: string;
          usage_count: number;
          limit: number;
          will_exceed: boolean;
        }> };
        
        // Filter IRM products to only those actually selected in treatmentProducts
        const selectedProductIds = new Set(treatmentProducts.map(p => p.product_id));
        const filteredProducts = selectedProductIds.size > 0
          ? result.products.filter(p => selectedProductIds.has(p.product_id))
          : result.products;
        setIrmWarnings(filteredProducts);
      } else {
        setIrmWarnings([]);
      }
    } catch (err: any) {
      console.error('IRM check exception:', err);
      setIrmError(err.message || 'Unknown error');
    } finally {
      setIrmLoading(false);
    }
  };

  // Get treatments filtered by selected pest
  const getFilteredTreatments = useCallback(() => {
    if (!formData.pest_id || formData.pest_id === "none") {
      return { treatments: treatments, hasAssociations: false };
    }
    
    const associatedIds = pestTreatmentMap.get(formData.pest_id);
    if (!associatedIds || associatedIds.size === 0) {
      return { treatments: treatments, hasAssociations: false };
    }
    
    const filtered = treatments.filter(t => associatedIds.has(t.id));
    return { treatments: filtered, hasAssociations: true };
  }, [treatments, pestTreatmentMap, formData.pest_id]);

  const handleLocationChange = (locationId: string) => {
    setSelectedLocation(locationId);
    setFormData({ ...formData, greenhouse_selections: [] });
  };

  const handlePestChange = (pestId: string) => {
    const newFormData = { ...formData, pest_id: pestId };
    
    if (pestId && pestId !== "none" && formData.treatment_id && formData.treatment_id !== "none") {
      const associatedIds = pestTreatmentMap.get(pestId);
      if (associatedIds && associatedIds.size > 0 && !associatedIds.has(formData.treatment_id)) {
        newFormData.treatment_id = "";
        setTreatmentProducts([]);
      }
    }
    
    setFormData(newFormData);
  };

  const handleTreatmentChange = async (treatmentId: string) => {
    const isRealTreatment = !!treatmentId && treatmentId !== "" && treatmentId !== "none";
    // Solo se corta cuando NO se ha elegido nada de sitio. Si hay invernaderos
    // elegidos, la ubicación se deduce de ellos (effectiveLocationId) y no hay nada
    // que preguntar; y si abarcan dos ubicaciones distintas, se deja elegir el
    // tratamiento igualmente y lo único que se salta es la comprobación de stock,
    // que necesita UNA ubicación concreta. Bloquear ahí sería castigar al usuario
    // por algo que ya nos había dicho.
    const nothingChosen =
      !effectiveLocationId && formData.greenhouse_selections.length === 0;
    if (isRealTreatment && locations.length > 0 && nothingChosen) {
      toast({
        title: t("planner.selectLocationFirst"),
        variant: "destructive",
      });
      return;
    }

    // If a specific location is selected and it's a real treatment, validate stock.
    // Note: at this point treatmentProducts may not be loaded yet — fetchTreatmentProducts
    // will run its own validation with required quantities once products are resolved.
    // This early call is only a soft pre-check (no requirements -> any positive stock passes).
    if (isRealTreatment && effectiveLocationId && company?.id) {
      try {
        const requirements = treatmentProducts.length > 0
          ? treatmentProducts.map(p => ({ product_id: p.product_id, required: p.amount || 0 }))
          : undefined;
        const result = await validateTreatmentLocationStock(company.id, treatmentId, effectiveLocationId, requirements);
        if (!result.valid) {
          setLocationStockWarningProducts(result.missingProducts);
          setShowLocationStockWarning(true);
        }
      } catch (err) {
        console.error('Error validating treatment location stock:', err);
      }
    }

    setFormData({ ...formData, treatment_id: treatmentId });
  };

  // Product handlers — edits change the rate; the absolute amount is re-derived.
  const handleProductRateValueChange = (index: number, value: number) => {
    setTreatmentProducts((prev) =>
      prev.map((p, i) => (i === index ? resolveProductRate({ ...p, rate_value: value }, totalAreaM2, caldoL) : p))
    );
  };

  const handleProductRateUnitChange = (index: number, unit: string) => {
    setTreatmentProducts((prev) =>
      prev.map((p, i) => (i === index ? resolveProductRate({ ...p, rate_unit: unit }, totalAreaM2, caldoL) : p))
    );
  };

  const handleRemoveProduct = (index: number) => {
    const updated = treatmentProducts.filter((_, i) => i !== index);
    setTreatmentProducts(updated);
  };

  const handleAddProduct = (productId: string) => {
    const invProduct = inventoryProducts.find(p => p.id === productId);
    if (!invProduct) return;

    if (treatmentProducts.some(p => p.product_id === productId)) {
      toast({ title: t("sprayLogs.productAlreadyAdded"), variant: "destructive" });
      return;
    }

    const stockUnit = invProduct.unit || 'L';
    const base: TreatmentProduct = {
      product_id: invProduct.id,
      product_name: invProduct.product_name,
      amount: 0,
      unit: stockUnit,
      stock: invProduct.quantity,
      is_from_treatment: false,
      rate_value: 1,
      rate_unit: stockUnit, // absolute by default; user can switch to a rate
      stock_unit: stockUnit,
      units_per_package: invProduct.units_per_package || null,
      original_unit: stockUnit,
    };
    setTreatmentProducts([...treatmentProducts, resolveProductRate(base, totalAreaM2, caldoL)]);
  };

  // Check if there are any row validation errors
  const hasRowErrors = Object.values(rowErrors).some(e => e.start || e.end);

  const handleSubmitWithWarning = async () => {
    if (!company?.id || !user?.id || !formData.title) {
      toast({ title: t("planner.fillRequiredFields"), variant: "destructive" });
      return;
    }

    if (hasRowErrors) {
      toast({ title: t("planner.fillRequiredFields"), variant: "destructive" });
      return;
    }

    // A product dosed per caldo (cc/100L, g/100L) requires a water volume to resolve.
    if (caldoMissing) {
      // Antes el aviso decía, literalmente, «Volumen de agua (opcional)».
      toast({ title: t("planner.waterVolumeRequiredMsg"), variant: "destructive" });
      return;
    }

    // Hard requirement on CREATE: a new task needs a specific greenhouse.
    // Edits are NOT re-gated for the greenhouse (they may predate this rule, and the manager
    // may just be tweaking a date/notes).
    // (Scouting-linked tasks carry their structure from the scouting session.)
    if (!task) {
      const fromScouting = !!prefilledData?.fromScouting || isScoutingLinked;
      const noGreenhouse = formData.greenhouse_selections.length === 0 && !fromScouting;
      if (noGreenhouse) {
        toast({ title: t("planner.greenhouseRequired"), variant: "destructive" });
        return;
      }
      // …y no basta el invernadero: cada uno debe llevar al menos un sector marcado.
      if (!fromScouting && formData.greenhouse_selections.some((s) => s.block_ids.length === 0)) {
        toast({ title: t("planner.sectorRequired"), variant: "destructive" });
        return;
      }
      // Responsable obligatorio: una tarea sin nadie asignado no la hace nadie.
      if (!formData.assigned_to) {
        toast({ title: t("planner.workerRequired"), variant: "destructive" });
        return;
      }
      // Tarea de tratamiento sin tratamiento (ni receta ni productos a mano) no se crea.
      if (formData.type === "treatment") {
        const hasTreatment = !!formData.treatment_id && formData.treatment_id !== "none";
        if (!hasTreatment && treatmentProducts.length === 0) {
          toast({ title: t("planner.treatmentRequiredMsg"), variant: "destructive" });
          return;
        }
      }
    }

    // El responsable SÍ se exige también al editar: una tarea sin nadie asignado no la hace
    // nadie. Hay tareas antiguas sin asignar (la opción existía), así que al abrir una de
    // esas para editarla habrá que elegir trabajador antes de poder guardar. Es intencionado.
    if (!formData.assigned_to) {
      toast({ title: t("planner.workerRequired"), variant: "destructive" });
      return;
    }

    // Auditoría del perito (26-09): una cantidad 0 no es un tratamiento —el servidor
    // descontaba 1 kg y el cuaderno decía «1 kg/ha»— (A14), y una dosis
    // disparatada se pregunta antes de guardar (A12).
    if (formData.type === "treatment") {
      const bad = treatmentProducts.find((p) => !(p.rate_value > 0) || !(p.amount > 0));
      if (bad) {
        const noArea = bad.rate_value > 0 && classifyUnit(bad.rate_unit) === "per_area";
        toast({ title: t(noArea ? "planner.areaMissingForPerHa" : "planner.amountMustBePositive"), variant: "destructive" });
        return;
      }
      if (!(await confirmDoses(treatmentProducts.map((p) => ({ product_id: p.product_id, product_name: p.product_name, rate_value: p.rate_value, rate_unit: p.rate_unit }))))) return;
    }

    await handleSubmitCore();
  };

  // Build observed_rows object for storage (only non-empty entries)
  const buildObservedRowsForStorage = (): Record<string, { row_start: number | null; row_end: number | null }> | null => {
    const result: Record<string, { row_start: number | null; row_end: number | null }> = {};
    for (const [blockId, data] of Object.entries(observedRows)) {
      // No se guardan líneas de un sector que no tiene líneas configuradas: acabarían en la
      // pantalla del trabajador como una instrucción imposible de cumplir. Los inputs ya
      // están desactivados, pero este estado puede venir de una tarea antigua o de la voz.
      const block = [...blocksByGreenhouse.values()].flat().find(b => b.id === blockId);
      if (block && !getLineRange(block)) continue;
      const rs = data.row_start ? parseInt(data.row_start) : null;
      const re = data.row_end ? parseInt(data.row_end) : null;
      if (rs != null || re != null) {
        result[blockId] = { row_start: isNaN(rs as number) ? null : rs, row_end: isNaN(re as number) ? null : re };
      }
    }
    return Object.keys(result).length > 0 ? result : null;
  };

  // Inject observed_rows into custom_products array
  const injectObservedRows = (products: any[] | null): any[] | null => {
    const rows = buildObservedRowsForStorage();
    if (!rows) return products;
    if (!products || products.length === 0) {
      return [{ observed_rows: rows }];
    }
    const updated = [...products];
    updated[0] = { ...updated[0], observed_rows: rows };
    return updated;
  };

  const handleSubmitCore = async () => {
    if (!company?.id || !user?.id || !formData.title) {
      toast({ title: t("planner.fillRequiredFields"), variant: "destructive" });
      return;
    }

    // ========== Validation: Scouting task must have block_id ==========
    // This prevents creating corrupted tasks from scouting sessions
    if (prefilledData?.fromScouting && !prefilledData.block_id) {
      console.error('BUG DETECTED: Creating task from scouting but block_id is missing in prefilledData', {
        scouting_session_id: prefilledData.scouting_session_id,
        greenhouse_id: prefilledData.greenhouse_id,
        block_id: prefilledData.block_id
      });
      toast({ 
        title: t("planner.errorMissingBlock"), 
        description: t("toasts.planner.sessionBlockIdMissing"),
        variant: "destructive" 
      });
      return;
    }
    // ===================================================================

    setLoading(true);
    try {
      // If editing a completed treatment task, use edge function
      if (task && task.status === 'completed' && formData.type === 'treatment' && treatmentProducts.length > 0) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          toast({ title: t("auth.signInError"), variant: "destructive" });
          return;
        }

        // Check if date changed — migrate dates across all related records
        const originalDate = task.scheduled_date ? format(new Date(task.scheduled_date), "yyyy-MM-dd") : null;
        const newDate = formData.scheduled_date.split("T")[0];
        if (originalDate && newDate !== originalDate) {
          const dateResponse = await supabase.functions.invoke("update-completed-task-date", {
            body: { task_id: task.id, new_date: formData.scheduled_date },
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (dateResponse.error) {
            console.error("Date migration failed:", dateResponse.error);
            toast({ title: t("planner.dateUpdateError"), variant: "destructive" });
            setLoading(false);
            return;
          }
        }

        const response = await supabase.functions.invoke("update-completed-task", {
          body: {
            task_id: task.id,
            // Con dosis, unidad y caldo: la corrección los conserva en el cuaderno (A15).
            updated_products: treatmentProducts.map(p => ({
              product_id: p.product_id,
              product_name: p.product_name,
              amount: p.unit === "pkg" && p.units_per_package ? p.amount * p.units_per_package : p.amount,
              unit: p.unit === "pkg" ? "units" : p.unit,
              unit_cost: (p as any).unit_cost || (p as any).unit_price || 0,
              rate_value: p.rate_value,
              rate_unit: p.rate_unit,
              stock_unit: p.stock_unit,
              dose_value: p.dose_value,
              dose_unit: p.dose_unit,
            }))
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (response.error) {
          // Falta de stock, permiso o cantidad: dicho en su idioma, no «non-2xx» (A15).
          let body: { error?: string } | null = null;
          try { body = await (response.error as { context?: { json?: () => Promise<{ error?: string }> } }).context?.json?.() ?? null; } catch { /* sin cuerpo */ }
          if (body?.error) throw new Error(isStockBlockCode(body.error) ? translateStockError(body) : completionErrorText(body, t) || body.error);
          throw response.error;
        }

        toast({ title: t("planner.taskUpdated") });
        onOpenChange(false);
        onSuccess();
        setLoading(false);
        return;
      }

      // Normal task creation/update flow
      /* EL TÍTULO DE UN TRATAMIENTO QUE NACE DE UN PARTE (usuario 14-09-2026). En
         este modo el formulario no enseña el campo título: lo pone la app con la
         regla de `buildAutoTaskTitle` —«Tratamiento: <plaga>», o el tratamiento
         configurado si no hay plaga, o «Tratamiento»—. Se calcula AQUÍ, al guardar,
         y no al abrir, porque el tratamiento se elige dentro del formulario. Al
         EDITAR no se toca: los títulos que ya existen se quedan como están. */
      const titleToSave = prefilledData?.fromScouting && !task
        ? buildAutoTaskTitle(
            {
              type: formData.type,
              pestName: (() => {
                const pestObj = pests.find((p) => p.id === formData.pest_id);
                return pestObj ? getPestName(pestObj) : prefilledData.pest_name;
              })(),
              treatmentName: treatments.find((tr) => tr.id === formData.treatment_id)?.name ?? null,
            },
            t,
          )
        : formData.title;
      // Create ONE task per greenhouse (not per block)
      const numGreenhouses = formData.greenhouse_selections.length || 1;
      
      const tasksToCreate: any[] = [];
      
      if (formData.greenhouse_selections.length === 0) {
        // No greenhouse selected - create single task
        const taskData: any = {
          company_id: company.id,
          title: titleToSave,
          type: formData.type,
          priority: formData.priority || "medium",
          greenhouse_id: null,
          block_id: null,
          scheduled_date: new Date(formData.scheduled_date).toISOString(),
          notes: formData.notes || null,
          assigned_to: formData.assigned_to || null,
        };

        if (!task) {
          taskData.created_by = user.id;
          taskData.status = "pending";
          // Solo al CREAR: editando no hay `prefilledData`, y escribir null aquí
          // soltaba la tarea de su parte (completarla ya no lo resolvía).
          taskData.scouting_session_id = prefilledData?.scouting_session_id || null;
        }

        if (formData.type === "treatment") {
          taskData.pest_id = formData.pest_id && formData.pest_id !== "none" ? formData.pest_id : null;
          taskData.treatment_id = formData.treatment_id && formData.treatment_id !== "none" ? formData.treatment_id : null;
          // Snapshot the pest name in the admin's currently-displayed language so notifications stay consistent
          if (taskData.pest_id) {
            const pestObj = pests.find(p => p.id === taskData.pest_id);
            taskData.pest_name_snapshot = pestObj ? getPestName(pestObj) : null;
          } else {
            taskData.pest_name_snapshot = null;
          }
          if (taskData.treatment_id) {
            const trObj = treatments.find(tt => tt.id === taskData.treatment_id);
            taskData.treatment_name_snapshot = trObj?.name || null;
          }
          
          if (treatmentProducts.length > 0) {
            taskData.custom_products = treatmentProducts.map((p, idx) => {
              // Convert pkg to units for backend
              const finalAmount = p.unit === "pkg" && p.units_per_package ? p.amount * p.units_per_package : p.amount;
              const finalUnit = p.unit === "pkg" ? "units" : p.unit;
              return {
                product_id: p.product_id,
                product_name: p.product_name,
                amount: finalAmount,
                unit: finalUnit,
                rate_value: p.rate_value,
                rate_unit: p.rate_unit,
                stock_unit: p.stock_unit,
                dose_value: p.dose_value,
                dose_unit: p.dose_unit,
                ...(idx === 0 && waterVolume != null ? (() => {
                  if (waterVolumeUnit === "L/ha") {
                    // Resolve L/ha to actual liters using total area of selected blocks
                    const allBlocks = formData.greenhouse_selections.flatMap(sel => {
                      const ghBlks = blocksByGreenhouse.get(sel.greenhouse_id) || [];
                      return sel.block_ids.length > 0 ? ghBlks.filter(b => sel.block_ids.includes(b.id)) : ghBlks;
                    });
                    const totalAreaM2 = allBlocks.reduce((s, b) => s + (b.area_m2 || 0), 0);
                    const resolved = totalAreaM2 > 0 ? parseFloat((waterVolume * (totalAreaM2 / 10000)).toFixed(2)) : waterVolume;
                    return { water_volume: resolved, water_volume_unit: "L", water_volume_per_ha: waterVolume };
                  }
                  return { water_volume: waterVolume, water_volume_unit: waterVolumeUnit };
                })() : {}),
              };
            });
          } else {
            taskData.custom_products = null;
          }
        }
        
        taskData.custom_products = injectObservedRows(taskData.custom_products);
        // La máquina elegida en el «⋯», solo si cambió (F2).
        Object.assign(taskData, appChoice.columns());
        tasksToCreate.push(taskData);
      } else {
        // Create one task per greenhouse
        for (const ghSelection of formData.greenhouse_selections) {
          // Determine block_id: use selection if single block, otherwise null
          let resolvedBlockId = ghSelection.block_ids.length === 1 ? ghSelection.block_ids[0] : null;
          
          // ========== FIX: Override block_id when coming from scouting ==========
          // When creating from scouting, ALWAYS use the original block_id from prefilledData
          // This prevents race conditions where greenhouse_selections might not be fully populated
          if (prefilledData?.fromScouting && prefilledData.block_id) {
            resolvedBlockId = prefilledData.block_id;
          }
          // ======================================================================
          
          // Populate name snapshots for location display
          const ghObj = greenhouses.find(g => g.id === ghSelection.greenhouse_id);
          const blockObj = resolvedBlockId
            ? (blocksByGreenhouse.get(ghSelection.greenhouse_id) || []).find(b => b.id === resolvedBlockId)
            : null;

          const taskData: any = {
            company_id: company.id,
            title: titleToSave,
            type: formData.type,
            priority: formData.priority || "medium",
            greenhouse_id: ghSelection.greenhouse_id,
            block_id: resolvedBlockId,
            greenhouse_name_snapshot: ghObj?.name || null,
            block_name_snapshot: blockObj?.name || null,
            scheduled_date: new Date(formData.scheduled_date).toISOString(),
            notes: formData.notes || null,
            assigned_to: formData.assigned_to || null,
          };

          if (!task) {
            taskData.created_by = user.id;
            taskData.status = "pending";
            // Solo al CREAR (ver arriba): editando, el enlace con el parte se queda.
            taskData.scouting_session_id = prefilledData?.scouting_session_id || null;
          }

          if (formData.type === "treatment") {
            taskData.pest_id = formData.pest_id && formData.pest_id !== "none" ? formData.pest_id : null;
            taskData.treatment_id = formData.treatment_id && formData.treatment_id !== "none" ? formData.treatment_id : null;
            if (taskData.pest_id) {
              const pestObj = pests.find(p => p.id === taskData.pest_id);
              taskData.pest_name_snapshot = pestObj ? getPestName(pestObj) : null;
            } else {
              taskData.pest_name_snapshot = null;
            }
            if (taskData.treatment_id) {
              const trObj = treatments.find(tt => tt.id === taskData.treatment_id);
              taskData.treatment_name_snapshot = trObj?.name || null;
            }
            
            // Divide products among greenhouses proportionally by area
            if (treatmentProducts.length > 0) {
              // Calculate area for this greenhouse
              const ghBlocks = blocksByGreenhouse.get(ghSelection.greenhouse_id) || [];
              const ghSelectedBlocks = ghSelection.block_ids.length > 0
                ? ghBlocks.filter(b => ghSelection.block_ids.includes(b.id))
                : ghBlocks;
              const ghAreaM2 = ghSelectedBlocks.reduce((sum, b) => sum + (b.area_m2 || 0), 0);

              // Calculate total area across all selected greenhouses
              const totalAreaM2 = formData.greenhouse_selections.reduce((total, sel) => {
                const blocks = blocksByGreenhouse.get(sel.greenhouse_id) || [];
                const selBlocks = sel.block_ids.length > 0
                  ? blocks.filter(b => sel.block_ids.includes(b.id))
                  : blocks;
                return total + selBlocks.reduce((s, b) => s + (b.area_m2 || 0), 0);
              }, 0);

              const proportion = totalAreaM2 > 0 ? ghAreaM2 / totalAreaM2 : 1 / numGreenhouses;

              taskData.custom_products = treatmentProducts.map((p, idx) => {
                const rawAmount = numGreenhouses > 1 ? parseFloat((p.amount * proportion).toFixed(4)) : p.amount;
                // Convert pkg to units for backend
                const finalAmount = p.unit === "pkg" && p.units_per_package ? rawAmount * p.units_per_package : rawAmount;
                const finalUnit = p.unit === "pkg" ? "units" : p.unit;
                return {
                  product_id: p.product_id,
                  product_name: p.product_name,
                  amount: finalAmount,
                  unit: finalUnit,
                  rate_value: p.rate_value,
                  rate_unit: p.rate_unit,
                  stock_unit: p.stock_unit,
                  dose_value: p.dose_value,
                  dose_unit: p.dose_unit,
                  target_blocks: ghSelection.block_ids,
                  ...(idx === 0 && waterVolume != null ? (() => {
                    if (waterVolumeUnit === "L/ha") {
                      // Resolve L/ha to actual liters for THIS greenhouse's area
                      const resolvedVol = ghAreaM2 > 0 ? parseFloat((waterVolume * (ghAreaM2 / 10000)).toFixed(2)) : waterVolume;
                      return { water_volume: resolvedVol, water_volume_unit: "L", water_volume_per_ha: waterVolume };
                    }
                    // Total liters: split across greenhouses proportionally by area
                    const resolvedVol = numGreenhouses > 1 ? parseFloat((waterVolume * proportion).toFixed(2)) : waterVolume;
                    return { water_volume: resolvedVol, water_volume_unit: "L" };
                  })() : {}),
                };
              });
            } else {
              // No products but multiple blocks selected - still store target_blocks
              if (ghSelection.block_ids.length > 1) {
                taskData.custom_products = [{ target_blocks: ghSelection.block_ids }];
              } else {
                taskData.custom_products = null;
              }
            }
          } else {
            // Non-treatment tasks: store target_blocks if multiple blocks selected
            if (ghSelection.block_ids.length > 1) {
              taskData.custom_products = [{ target_blocks: ghSelection.block_ids }];
            }
          }
          
          taskData.custom_products = injectObservedRows(taskData.custom_products);
          // La máquina elegida en el «⋯», solo si cambió (F2): la misma en todas las naves.
          Object.assign(taskData, appChoice.columns());
          tasksToCreate.push(taskData);
        }
      }

      // Guard: check auth session is still valid before writing
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        toast({
          title: t("common.error"),
          description: t("auth.sessionExpired"),
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      let error;
      if (task) {
        // When editing, only update the single task
        const result = await supabase
          .from("planner_tasks")
          .update(tasksToCreate[0])
          .eq("id", task.id);
        error = result.error;
      } else {
        // Insert all tasks
        if (prefilledData?.scouting_session_id) {
          // Creating from scouting - MUST link task to session
          const result = await supabase
            .from("planner_tasks")
            .insert(tasksToCreate[0])
            .select('id')
            .single();
          error = result.error;
          
          if (!error && result.data) {
            const taskId = result.data.id;
            const { error: updateError } = await supabase
              .from("pest_scouting_sessions")
              .update({
                review_status: 'in_review',
                linked_task_id: taskId
              })
              .eq("id", prefilledData.scouting_session_id);
            // El parte pasa a «previsto»: mapa, alertas e informes lo tienen que saber ya.
            invalidateScoutingData(queryClient);
            
            if (updateError) {
              console.error("Error linking task to scouting session:", updateError);
              toast({
                title: t("common.warning"),
                description: t("toasts.planner.scoutingLinkFailed"),
                variant: "destructive",
              });
            }
          }
        } else {
          // No scouting session - just insert all tasks
          const result = await supabase
            .from("planner_tasks")
            .insert(tasksToCreate);
          error = result.error;
        }
      }

      if (error) throw error;

      const message = tasksToCreate.length > 1 
        ? t("planner.tasksCreated", { count: tasksToCreate.length })
        : task 
          ? t("planner.taskUpdated") 
          : prefilledData?.scouting_session_id 
            ? t("pests.scouting.taskCreatedSuccess") 
            : t("planner.taskCreated");
            
      toast({ title: message });
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      logDatabaseError(error, "TaskFormDialog.handleSubmit", {
        user_id: user?.id,
        company_id: company?.id,
        entity_type: "planner_task",
        entity_id: task?.id,
        operation: task ? "update" : "insert",
        table: "planner_tasks",
      });
      toast({ 
        title: task ? t("planner.errorUpdating") : t("planner.errorCreating"), 
        description: error.message,
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  const { treatments: filteredTreatments, hasAssociations } = getFilteredTreatments();
  const showNoAssociationsNote = formData.pest_id && formData.pest_id !== "none" && !hasAssociations;

  /* «CONTRA QUÉ PLAGA», SUGERIDA (F9, 26-09-2026; D9). La Orden APA/326/2007
     pide la plaga de cada tratamiento y solo la llevaba el 13 % de las líneas
     de clientes: la tarea se creaba con receta y sin plaga, y la línea del parte
     copia la de la tarea (complete-treatment-task). Aquí se SUGIERE —un toque,
     nunca se pone sola— desde el parte o foco de origen y desde la receta
     elegida. Una tarea completada no: su plaga ya está en el parte. */
  const pestSuggestionsOn =
    open && formData.type === "treatment" && !hasPest(formData.pest_id) && task?.status !== "completed";
  const originSessionId = prefilledData?.scouting_session_id ?? task?.scouting_session_id ?? null;
  const focusPestIds = useScoutingPestIds(originSessionId, pestSuggestionsOn);
  const pestSuggestions = useMemo(() => {
    if (!pestSuggestionsOn) return [];
    const order = pests.map((p) => p.id);
    return suggestTaskPests({
      currentPestId: formData.pest_id,
      focusPestIds,
      recipePestIds: recipePestIds(pestTreatmentMap, formData.treatment_id, order),
      knownPestIds: new Set(order),
    });
  }, [pestSuggestionsOn, pests, formData.pest_id, formData.treatment_id, focusPestIds, pestTreatmentMap]);
  /* Tocar una sugerencia NO pasa por `handlePestChange`: aquél quita la receta
     si no está asociada a la plaga, y quien toca «Tuta · del parte» con su
     receta ya elegida no está pidiendo que se la borren. */
  const pickSuggestedPest = (pestId: string) => setFormData((prev) => ({ ...prev, pest_id: pestId }));

  /* AVISOS AL ELEGIR PRODUCTO, APLICADOR Y MÁQUINA (F4, 26-09-2026; D2). Nunca
     bloquean (T4): se enseñan en el paso del tratamiento y la tarea se guarda
     igual. Producto: lo que le pasa a su nº de registro (H1/H2), con el
     catálogo del MAPA que se pide al tener productos en la tarea (una vez por
     sesión) y apagado con el Registro MAPA de la empresa (D26). Aplicador y
     máquina (H6/H7): los que saldrán en el parte —el elegido en el «⋯» o, sin
     elegir, el asignado y la máquina por defecto de la finca, con la MISMA regla
     que el servidor (`pickApplicator`, `pickEquipment`)—, el día programado.
     Solo quien ve el «⋯» (admin y encargado) tiene las listas. */
  const { off: registroMapaOff, loading: registroMapaLoading } = useRegistroMapaOff();
  const registroMapaOn = !registroMapaOff && !registroMapaLoading;
  const { items: registryItems, loading: registryLoading } = useRegistryCatalog(
    open && formData.type === "treatment" && registroMapaOn && treatmentProducts.length > 0,
  );
  const registryIndex = useMemo(
    () => (registryItems.length > 0 ? buildRegistryIndex(registryItems) : null),
    [registryItems],
  );
  const productRegistrationChecks = useMemo(() => {
    const out = new Map<string, RegistrationCheck>();
    if (formData.type !== "treatment") return out;
    for (const p of treatmentProducts) {
      const inv = inventoryProducts.find((ip) => ip.id === p.product_id);
      if (!inv) continue;
      out.set(
        p.product_id,
        validateRegistration(
          { registrationNumber: inv.registration_number, productName: inv.product_name, category: inv.category },
          { catalog: registryIndex, mapaValidation: registroMapaOn },
        ),
      );
    }
    return out;
  }, [formData.type, treatmentProducts, inventoryProducts, registryIndex, registroMapaOn]);
  const renderProductNote = useCallback(
    (productId: string) => (
      <RegistrationCheckNote
        check={productRegistrationChecks.get(productId)}
        showNoData={!registryLoading && registroMapaOn}
      />
    ),
    [productRegistrationChecks, registryLoading, registroMapaOn],
  );
  const taskApplicationChecks = useMemo(() => {
    if (formData.type !== "treatment" || !appChoice.enabled) return [];
    const picked = pickApplicator(appChoice.applicators, {
      chosenId: appChoice.applicatorId,
      assignedUserId: formData.assigned_to || null,
    });
    const equipment = pickEquipment(appChoice.equipment, {
      chosenId: appChoice.equipmentId,
      locationId: effectiveLocationId,
    });
    return applicationChecks({
      applicator:
        picked.applicator || picked.userId
          ? {
              row: picked.applicator,
              personName: companyWorkers.find((w) => w.user_id === picked.userId)?.full_name ?? null,
            }
          : null,
      equipment,
      date: (formData.scheduled_date || "").slice(0, 10),
    });
  }, [
    formData.type,
    formData.assigned_to,
    formData.scheduled_date,
    appChoice.enabled,
    appChoice.applicators,
    appChoice.applicatorId,
    appChoice.equipment,
    appChoice.equipmentId,
    effectiveLocationId,
    companyWorkers,
  ]);

  // Calculate totals for products section
  const numGreenhouses = formData.greenhouse_selections.length || 1;
  const totalBlocks = formData.greenhouse_selections.reduce((sum, s) => sum + s.block_ids.length, 0);
  const hasBlocksSelected = totalBlocks > 0 || formData.greenhouse_selections.length > 0;

  // Calculate greenhouse area breakdown for proportional distribution
  const greenhouseAreaBreakdown = useMemo(() => {
    if (formData.greenhouse_selections.length <= 1) return undefined;
    return formData.greenhouse_selections.map(sel => {
      const blocks = blocksByGreenhouse.get(sel.greenhouse_id) || [];
      const selectedBlocks = sel.block_ids.length > 0
        ? blocks.filter(b => sel.block_ids.includes(b.id))
        : blocks;
      const areaM2 = selectedBlocks.reduce((sum, b) => sum + (b.area_m2 || 0), 0);
      return {
        greenhouse_name: sel.greenhouse_name,
        area_ha: areaM2 / 10000,
      };
    });
  }, [formData.greenhouse_selections, blocksByGreenhouse]);

  // Show products section when:
  // 1. A treatment is selected (will load its products)
  // 2. Or "none" treatment is selected (manual products mode)
  // 3. Or we're in a treatment task type and customizing
  const showProductsSection = formData.type === "treatment" && (
    formData.treatment_id === "none" || 
    (formData.treatment_id && formData.treatment_id !== "") ||
    isCustomizing
  );

  // ========== WIZARD STEPS DEFINITION ==========
  const wizardSteps: WizardStep[] = useMemo(() => {
    const getWorkerName = (id: string) => companyWorkers.find(w => w.user_id === id)?.full_name || "";
    const getPestNameById = (id: string) => {
      const pest = pests.find(p => p.id === id);
      return pest ? getPestName(pest) : "";
    };
    const getTreatmentName = (id: string) => treatments.find(t => t.id === id)?.name || "";
    const getLocationSummary = () => {
      if (formData.greenhouse_selections.length === 0) return null;
      const gh = formData.greenhouse_selections[0];
      const blocksCount = formData.greenhouse_selections.reduce((sum, s) => sum + s.block_ids.length, 0);
      if (formData.greenhouse_selections.length === 1) {
        return `${gh.greenhouse_name}${blocksCount > 0 ? ` (${blocksCount} ${blocksCount === 1 ? t("common.task") : t("common.tasks").split(' ')[0]})` : ""}`;
      }
      return `${formData.greenhouse_selections.length} ${t("settings.structure.greenhouses")}`;
    };

    return [
      {
        id: "title",
        titleKey: "planner.taskTitle",
        isRequired: true,
        isVisible: !prefilledData?.fromScouting && !isScoutingLinked,
        getSummary: () => formData.title || null,
        isCompleted: () => !!formData.title.trim(),
        content: (
          <div>
            <Label>{t("planner.taskTitle")}</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={t("planner.taskTitlePlaceholder")}
              className="mt-1"
              ref={desktopAutoFocusRef}
              disabled={isScoutingLinked}
            />
          </div>
        )
      },
      {
        id: "type-priority",
        titleKey: prefilledData?.fromScouting ? "planner.priorityLabel" : "planner.type",
        isRequired: false,
        isVisible: true,
        getSummary: () => prefilledData?.fromScouting
          ? t(`planner.priorities.${formData.priority}`)
          : formData.priority
            ? `${t(`planner.types.${formData.type}`)} · ${t(`planner.priorities.${formData.priority}`)}`
            : t(`planner.types.${formData.type}`),
        isCompleted: () => !!formData.priority,
        content: prefilledData?.fromScouting ? (
          <div className="space-y-2">
            <div className="flex gap-2">
              {(["low", "medium", "high"] as const).map((p) => (
                <Button
                  key={p}
                  type="button"
                  variant={formData.priority === p ? "default" : "outline"}
                  size="sm"
                  className={cn(
                    "flex-1 transition-all",
                    formData.priority === p && "ring-2 ring-primary/30"
                  )}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, priority: p }));
                    // Auto-advance to next step after a brief visual feedback
                    setTimeout(() => handleWizardContinue(), 200);
                  }}
                >
                  {t(`planner.priorities.${p}`)}
                </Button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>{t("planner.type")}</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })} disabled={isScoutingLinked}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="treatment">{t("planner.types.treatment")}</SelectItem>
                  <SelectItem value="monitoring">{t("planner.types.monitoring")}</SelectItem>
                  <SelectItem value="maintenance">{t("planner.types.maintenance")}</SelectItem>
                  <SelectItem value="visit">{t("planner.types.visit")}</SelectItem>
                  <SelectItem value="inspection">{t("planner.types.inspection")}</SelectItem>
                  <SelectItem value="other">{t("planner.types.other")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Prioridad: tres botones en una fila bajo el Tipo. Ninguno viene
                preseleccionado en una tarea nueva (priority = ""); al editar,
                el color de la prioridad guardada ya aparece marcado. */}
            <div>
              <Label>{t("planner.priorityLabel")}</Label>
              <div className="mt-1 flex gap-2">
                {([
                  { value: "low", active: "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-600 hover:text-white" },
                  { value: "medium", active: "bg-amber-400 text-amber-950 border-amber-400 hover:bg-amber-400 hover:text-amber-950" },
                  { value: "high", active: "bg-red-600 text-white border-red-600 hover:bg-red-600 hover:text-white" },
                ] as const).map(({ value, active }) => {
                  const selected = formData.priority === value;
                  return (
                    <Button
                      key={value}
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-pressed={selected}
                      className={cn("flex-1 transition-all", selected && active)}
                      onClick={() => setFormData(prev => ({ ...prev, priority: value }))}
                    >
                      {t(`planner.priorities.${value}`)}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        )
      },
      
      {
        id: "location",
        titleKey: "planner.location",
        // Obligatorio, y no basta el invernadero: hace falta saber al menos en qué
        // sector se trabaja. Cada invernadero elegido debe llevar ≥1 sector marcado,
        // para que no se cuele uno a medio elegir.
        isRequired: true,
        // Always visible — including when prefilled from scouting — so the
        // manager can confirm or change the location of the new task.
        isVisible: !isScoutingLinked,
        getSummary: getLocationSummary,
        isCompleted: () =>
          formData.greenhouse_selections.length > 0 &&
          formData.greenhouse_selections.every((s) => s.block_ids.length > 0),
        content: (
          <div className="space-y-4">
            <div>
              <Label>{t("planner.location")}</Label>
              <Select value={selectedLocation} onValueChange={handleLocationChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t("planner.selectLocation")} />
                </SelectTrigger>
                <SelectContent>
                  {locations.length === 0 ? (
                    <EmptySelectState
                      type="location"
                      message={t("common.emptyStates.noLocations")}
                      hint={t("common.emptyStates.noLocationsHint")}
                      actionLabel={t("common.emptyStates.goToSettings")}
                    />
                  ) : (
                    <>
                      <SelectItem value="all">{t("planner.allLocations")}</SelectItem>
                      {locations.map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                      ))}
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            {selectedLocation && (
              <MultiGreenhouseSelector
                greenhouses={greenhouses}
                blocksByGreenhouse={blocksByGreenhouse}
                blockCyclesMap={blockCyclesMap}
                selections={formData.greenhouse_selections}
                onSelectionsChange={(selections) => setFormData({ ...formData, greenhouse_selections: selections })}
                formatCycleLabel={formatCycleLabel}
                locationFilter={selectedLocation}
                isEditing={!!task}
              />
            )}
            {/* Optional row inputs */}
            {formData.greenhouse_selections.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Rows className="w-4 h-4 text-muted-foreground" />
                    <Label className="text-sm font-medium">{t("planner.rowsLabel")}</Label>
                    <span className="text-xs text-muted-foreground">({t("common.optional")})</span>
                  </div>
                  <Switch checked={showRowInputs} onCheckedChange={setShowRowInputs} />
                </div>
                {showRowInputs && (
                  <div className="space-y-3 mt-2">
                    {formData.greenhouse_selections.map(sel => {
                      const blocks = blocksByGreenhouse.get(sel.greenhouse_id) || [];
                      const selectedBlocks = sel.block_ids.length > 0
                        ? blocks.filter(b => sel.block_ids.includes(b.id))
                        : [];
                      if (selectedBlocks.length === 0) return null;
                      return (
                        <div key={sel.greenhouse_id} className="space-y-2">
                          {formData.greenhouse_selections.length > 1 && (
                            <span className="text-xs font-semibold text-muted-foreground uppercase">{greenhouses.find(g => g.id === sel.greenhouse_id)?.name || sel.greenhouse_id}</span>
                          )}
                          {selectedBlocks.map(block => {
                            // Rango efectivo: el configurado, o 1..lines_count si solo hay contador.
                            const lineRange = getLineRange(block);
                            const hasRowRange = lineRange !== null;
                            const rowData = observedRows[block.id] || { row_start: "", row_end: "" };
                            const blockErrors = rowErrors[block.id];
                            return (
                              <div key={block.id} className="space-y-1">
                                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                                  <Rows className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                  <span className="text-xs font-medium text-muted-foreground max-w-[50px] sm:max-w-[80px] truncate">{block.name}</span>
                                  <Input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    placeholder={t("pests.scouting.observedRowStart")}
                                    value={rowData.row_start}
                                    onChange={(e) => {
                                      const val = e.target.value.replace(/[^0-9]/g, "");
                                      setObservedRows(prev => ({ ...prev, [block.id]: { ...rowData, row_start: val } }));
                                      if (lineRange && val !== "") {
                                        const num = parseInt(val);
                                        if (!isNaN(num) && (num < lineRange.min || num > lineRange.max)) {
                                          setRowErrors(prev => ({ ...prev, [block.id]: { ...prev[block.id], start: t("planner.rowOutOfRange", { min: lineRange.min, max: lineRange.max }) } }));
                                        } else {
                                          setRowErrors(prev => ({ ...prev, [block.id]: { ...prev[block.id], start: undefined } }));
                                        }
                                      } else {
                                        setRowErrors(prev => ({ ...prev, [block.id]: { ...prev[block.id], start: undefined } }));
                                      }
                                    }}
                                    disabled={!lineRange}
                                    className={cn("w-16 sm:w-20 h-10 text-base sm:text-sm", blockErrors?.start && "border-destructive", !lineRange && "opacity-50")}
                                  />
                                  <span className="text-xs text-muted-foreground">—</span>
                                  <Input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    placeholder={t("pests.scouting.observedRowEnd")}
                                    value={rowData.row_end}
                                    onChange={(e) => {
                                      const val = e.target.value.replace(/[^0-9]/g, "");
                                      setObservedRows(prev => ({ ...prev, [block.id]: { ...rowData, row_end: val } }));
                                      if (lineRange && val !== "") {
                                        const num = parseInt(val);
                                        if (!isNaN(num) && (num < lineRange.min || num > lineRange.max)) {
                                          setRowErrors(prev => ({ ...prev, [block.id]: { ...prev[block.id], end: t("planner.rowOutOfRange", { min: lineRange.min, max: lineRange.max }) } }));
                                        } else {
                                          setRowErrors(prev => ({ ...prev, [block.id]: { ...prev[block.id], end: undefined } }));
                                        }
                                      } else {
                                        setRowErrors(prev => ({ ...prev, [block.id]: { ...prev[block.id], end: undefined } }));
                                      }
                                    }}
                                    disabled={!lineRange}
                                    className={cn("w-16 sm:w-20 h-10 text-base sm:text-sm", blockErrors?.end && "border-destructive", !lineRange && "opacity-50")}
                                  />
                                </div>
                                <p className={cn("text-[10px] ml-5", lineRange ? "text-muted-foreground" : "text-amber-700")}>
                                  {lineRange
                                    ? `${t("common.rows")}: ${lineRange.min} – ${lineRange.max}`
                                    : t("planner.linesNotSetUp")}
                                </p>
                                {(blockErrors?.start || blockErrors?.end) && (
                                  <p className="text-xs text-destructive ml-5">{blockErrors.start || blockErrors.end}</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      },
      {
        id: "assigned-to",
        titleKey: "planner.assignedTo",
        // Obligatorio: una tarea sin responsable no la hace nadie. Antes se podía pasar
        // de paso sin elegirlo y la tarea nacía huérfana.
        isRequired: true,
        isVisible: true,
        getSummary: () => formData.assigned_to ? getWorkerName(formData.assigned_to) : t("planner.unassigned"),
        isCompleted: () => !!formData.assigned_to,
        content: (() => {
          const isVisitOrInspection = formData.type === "visit" || formData.type === "inspection";
          /* «ASIGNAR A» SOLO OFRECE A SU GENTE (24-09-2026). Ofrecía la
             plantilla entera: un encargado de Níjar podía mandarle trabajo a
             los de Mojonera. Quien no tiene finca concedida sigue saliendo
             para todos —es el contrato de member_scope—, así que en una
             empresa que no reparte fincas este desplegable no cambia ni un
             nombre.

             `companyWorkers` SE QUEDA ENTERO a propósito: de ahí sale el
             NOMBRE del asignado en el resumen y en la ficha, y un nombre que
             no se resuelve deja la tarjeta en blanco. Lo que se recorta es lo
             que se OFRECE.

             Y quien YA está asignado no se cae nunca de la lista: al editar
             una tarea vieja, cuyo asignado puede ser de otra finca, quitarlo
             del desplegable lo borraría sin querer al guardar. */
          const puedeOfrecerse = (w: CompanyWorker) =>
            esVisible(w.user_id) || w.user_id === formData.assigned_to;
          const filteredWorkers = isVisitOrInspection
            ? companyWorkers.filter(w => w.role === "perito" && puedeOfrecerse(w))
            : companyWorkers.filter(w => (w.role === "worker" || w.role === "manager") && puedeOfrecerse(w));

          // Con la ubicación ya elegida —al editar, o al crear desde un aviso—,
          // primero y a un toque los asignados a esos sectores; el resto, plegado
          // en "Otros". En una tarea nueva aún no hay sectores, así que no hay
          // nada que agrupar y sale la lista entera.
          // Los peritos no viven en block_worker_assignments: su rama no agrupa.
          const hasBlocks = formData.greenhouse_selections.some(s => s.block_ids.length > 0);
          const assignedWorkers = !isVisitOrInspection && hasBlocks
            ? filteredWorkers.filter(w => blockAssignedUserIds.has(w.user_id))
            : [];
          const useGrouping = assignedWorkers.length > 0;
          const otherWorkers = useGrouping
            ? filteredWorkers.filter(w => !blockAssignedUserIds.has(w.user_id))
            : filteredWorkers;
          return (
            <div>
              <Label>{t("planner.assignedTo")} <span className="text-black">*</span></Label>
              {isVisitOrInspection && filteredWorkers.length === 0 ? (
                <div className="mt-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    {t("perito.seguimiento.noPeritoAssigned")}
                  </p>
                </div>
              ) : (
                <>
                  {!isVisitOrInspection && hasBlocks && !useGrouping && (
                    <p className="mt-1 text-xs text-muted-foreground">{t("planner.noOneAssignedHint")}</p>
                  )}
                  <Select
                    value={formData.assigned_to}
                    onValueChange={(value) => {
                      setFormData({ ...formData, assigned_to: value });
                      setManuallyAssigned(true);
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder={t("planner.selectWorker")} />
                    </SelectTrigger>
                    {/* Sin opción "Sin asignar": una tarea sin responsable no la hace nadie.
                        El placeholder pide elegir hasta que haya trabajador.

                        UN SOLO ASPECTO, con y sin ubicación. Aquí había además una
                        lista de tarjetas que solo salía cuando ya se sabía la
                        ubicación, así que crear una tarea y editarla enseñaban dos
                        selectores distintos para el mismo dato — y el de editar,
                        siete tarjetas de alto, tapaba el resto del paso.
                        Lo que sí se conserva es el ORDEN, que es lo que resolvía el
                        problema de Nijasol: con la ubicación puesta, los asignados a
                        esos sectores salen primero y bajo su propio encabezado, en
                        vez de perdidos entre los dieciséis del equipo. */}
                    <SelectContent>
                      {useGrouping ? (
                        <>
                          <SelectGroup>
                            <SelectLabel>{t("planner.assignedToLocation")}</SelectLabel>
                            {assignedWorkers.map(w => (
                              <SelectItem key={w.user_id} value={w.user_id}>
                                {w.full_name} ({t(`team.roles.${w.role}`)})
                              </SelectItem>
                            ))}
                          </SelectGroup>
                          {otherWorkers.length > 0 && (
                            <SelectGroup>
                              <SelectLabel>{t("planner.othersGroup")}</SelectLabel>
                              {otherWorkers.map(w => (
                                <SelectItem key={w.user_id} value={w.user_id}>
                                  {w.full_name} ({t(`team.roles.${w.role}`)})
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                        </>
                      ) : (
                        filteredWorkers.map(w => (
                          <SelectItem key={w.user_id} value={w.user_id}>
                            {w.full_name} ({t(`team.roles.${w.role}`)})
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
          );
        })()
      },
      {
        id: "pest-treatment",
        titleKey: "planner.treatment",
        // Para una tarea de tratamiento, el tratamiento es obligatorio (la plaga no):
        // vale una receta configurada o productos añadidos a mano. Y si algún producto
        // se dosifica por agua, el volumen de agua también es obligatorio aquí.
        isRequired: true,
        isVisible: formData.type === "treatment",
        getSummary: () => {
          const parts: string[] = [];
          if (formData.pest_id && formData.pest_id !== "none") {
            parts.push(getPestNameById(formData.pest_id));
          }
          if (formData.treatment_id && formData.treatment_id !== "none") {
            parts.push(getTreatmentName(formData.treatment_id));
          }
          return parts.length > 0 ? parts.join(" · ") : null;
        },
        isCompleted: () => {
          const hasTreatment = !!formData.treatment_id && formData.treatment_id !== "none";
          const hasCustomProducts = treatmentProducts.length > 0;
          if (!hasTreatment && !hasCustomProducts) return false;
          return !caldoMissing;
        },
        content: (
          <div className="space-y-4">
            {/* Pest Selector */}
            <div>
              <Label>{t("planner.pestAgainst", "Contra qué plaga")} <span className="text-muted-foreground text-xs">({t("common.optional")})</span></Label>
              {/* Una tarea que nace de un parte no cambia de plaga; pero si nació SIN
                  ella, se le puede poner (F9): el vínculo es el parte, no la plaga. */}
              <Select value={formData.pest_id} onValueChange={handlePestChange} disabled={isScoutingLinked && hasPest(task?.pest_id)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t("planner.selectPest")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("planner.noPest")}</SelectItem>
                  {pests.map(p => (
                    <SelectItem key={p.id} value={p.id}>{getPestName(p)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <PestSuggestionChips
                className="mt-1.5"
                suggestions={pestSuggestions}
                pestName={(id) => {
                  const pest = pests.find((p) => p.id === id);
                  return pest ? getPestName(pest) : null;
                }}
                onPick={pickSuggestedPest}
              />
            </div>
            {/* Treatment Selector */}
            <div>
              <Label>{t("planner.treatment")} <span className="text-muted-foreground text-xs">({t("common.optional")})</span></Label>
              <Select value={formData.treatment_id} onValueChange={handleTreatmentChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t("planner.selectTreatment")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("planner.manualProducts")}</SelectItem>
                  {filteredTreatments.map(tr => (
                    <SelectItem key={tr.id} value={tr.id} className="[&>span:last-child]:w-full">
                      {/* NI EL NOMBRE NI LA PÍLDORA PUEDEN ENSANCHAR LA FILA
                          (informe del usuario 31-08-2026: la lista se salía por
                          la derecha y las píldoras quedaban cortadas).

                          Las dos mitades se negaban a encoger: un hijo de un
                          flex tiene `min-width: auto`, así que `truncate` sobre
                          el nombre no servía de nada —recorta cuando el hueco
                          es menor, y sin `min-w-0` el hueco nunca lo era— y la
                          píldora lleva `whitespace-nowrap` de serie. Entre las
                          dos, la fila medía lo que midiera el nombre más la
                          píldora entera, pasara de largo del desplegable o no.

                          `min-w-0` en el nombre (que es lo que puede ceder) y
                          `shrink-0` en la píldora (que no debe: media palabra de
                          «Biológico» no dice nada). */}
                      <span className="flex w-full min-w-0 items-center justify-between gap-3">
                        <span className="min-w-0 truncate">{tr.name}</span>
                        {(tr.mode === "biological" || tr.mode === "chemical") && (
                          <span className={cn(PILL_BASE, "shrink-0")} style={{ backgroundColor: tr.mode === "biological" ? PILL_COLORS.green : PILL_COLORS.red }}>
                            {tr.mode === "biological" ? t("pests.biological") : t("pests.chemical")}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {showNoAssociationsNote && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                  <Info className="w-3 h-3" />
                  <span>{t("pests.noAssociationsShowingAll")}</span>
                </div>
              )}
            </div>
            {/* Products Section */}
            {showProductsSection && (
              <TreatmentProductsSection
                stockFlexible={stockFlexible}
                products={treatmentProducts}
                inventoryProducts={filteredInventoryProducts}
                isCustomizing={isCustomizing}
                onCustomizingChange={setIsCustomizing}
                onProductRateValueChange={handleProductRateValueChange}
                onProductRateUnitChange={handleProductRateUnitChange}
                onRemoveProduct={handleRemoveProduct}
                onAddProduct={handleAddProduct}
                numGreenhouses={numGreenhouses}
                totalBlocks={totalBlocks}
                showAlwaysVisible={formData.treatment_id === "none"}
                totalAreaHa={totalAreaHa}
                caldoL={caldoL}
                caldoMissing={caldoMissing}
                greenhouseAreaBreakdown={greenhouseAreaBreakdown}
                waterVolume={waterVolume}
                waterVolumeUnit={waterVolumeUnit}
                onWaterVolumeChange={setWaterVolume}
                onWaterVolumeUnitChange={setWaterVolumeUnit}
                renderProductNote={renderProductNote}
              />
            )}
            {/* F4: carné del aplicador y máquina (H6/H7). No bloquea. */}
            <ApplicationChecksNote checks={taskApplicationChecks} />
            </div>
          )
      },

      {
        id: "schedule",
        titleKey: "planner.scheduledDateTime",
        isRequired: false,
        isVisible: true,
        getSummary: () => formData.scheduled_date ? format(new Date(formData.scheduled_date), "MMM d, yyyy HH:mm") : null,
        isCompleted: () => !!formData.scheduled_date,
         content: (() => {
           const { date: selDate, time: selTime } = parseScheduledDateTime(formData.scheduled_date);
           const dateValue = selDate ? format(selDate, "yyyy-MM-dd") : "";
           // px-2.5 + 13px en móvil: con Fecha y Hora a la vez en una fila de teléfono,
           // cada caja se queda en ~160px y con el padding de antes «31 jul 2026» no cabía.
           // Que el valor caiga centrado en el alto de la caja (y no pegado arriba, que
           // era el fallo del iPhone) lo resuelve ya una regla global de src/index.css,
           // así que aquí no hace falta repetirlo: un input de fecha nuevo lo hereda sin
           // que nadie se acuerde. Lo que SÍ se queda es el `m-0`, que es de esta fila y
           // no de todas: aquí Fecha y Hora comparten ancho de teléfono, cada caja se
           // queda en ~160 px y sin quitarle a iOS el margen que le pone al valor
           // «31 jul 2026» no entra. Global sería descuadrar siete campos que nadie ha
           // dado por rotos.
           const inputBase =
             "mt-1 block w-full max-w-full h-11 text-[13px] sm:text-sm leading-none rounded-md bg-white shadow-sm border border-[#b6dded] px-2.5 sm:px-3 text-left appearance-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-date-and-time-value]:m-0";
           return (
             <div className="w-full max-w-full min-w-0 overflow-hidden space-y-3">
               {/* Fecha y Hora SIEMPRE en la misma fila, también en teléfono. */}
               <div className="grid grid-cols-2 gap-2.5 sm:gap-3 items-stretch">
                 <div className="min-w-0">
                   <Label>{t("common.date")}</Label>
                   <input
                     type="date"
                     value={dateValue}
                     onChange={(e) => {
                       const v = e.target.value;
                       if (!v) return;
                       const d = new Date(v + "T00:00:00");
                       if (isValid(d)) {
                         setFormData({ ...formData, scheduled_date: setScheduledDateTime(d, selTime) });
                       }
                     }}
                     className={inputBase}
                   />
                 </div>
                 <div className="min-w-0">
                   <Label>{t("common.time")}</Label>
                   <input
                     type="time"
                     value={selTime}
                     onChange={(e) => {
                       if (selDate) {
                         setFormData({ ...formData, scheduled_date: setScheduledDateTime(selDate, e.target.value) });
                       }
                     }}
                     className={inputBase}
                   />
                 </div>
               </div>
             </div>
           );
         })()
       },
       {
         id: "notes",
         titleKey: "planner.notes",
         isRequired: false,
         isVisible: true,
         getSummary: () => formData.notes ? (formData.notes.length > 40 ? `${formData.notes.slice(0, 40)}...` : formData.notes) : null,
         isCompleted: () => true,
         // Plegadas por defecto: una línea «Notas (opcional) +» y la caja solo si se
         // pide. Si la tarea ya trae notas (edición), la caja sale abierta para no
         // esconder lo escrito.
         content: notesOpen || !!formData.notes.trim() ? (
           <div>
             <Label>{t("planner.notes")} <span className="text-muted-foreground text-xs">({t("common.optional")})</span></Label>
             <Textarea
               value={formData.notes}
               onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
               placeholder={t("planner.notesPlaceholder")}
               className="mt-1 bg-white shadow-sm border border-[#b6dded] rounded-md focus-visible:ring-2 focus-visible:ring-ring"
               rows={3}
               autoFocus={notesOpen && !formData.notes.trim()}
             />
           </div>
         ) : (
           <button
             type="button"
             onClick={() => setNotesOpen(true)}
             className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
           >
             <span className="w-6 h-6 rounded-full border border-[#b6dded] bg-white shadow-sm flex items-center justify-center">
               <Plus className="w-3.5 h-3.5" />
             </span>
             {t("planner.notes")} <span className="text-muted-foreground text-xs">({t("common.optional")})</span>
           </button>
         )
       }
    ];
  }, [formData, companyWorkers, pests, treatments, locations, greenhouses, blocksByGreenhouse, selectedLocation, prefilledData, blockCyclesMap, filteredTreatments, showNoAssociationsNote, pestSuggestions, renderProductNote, taskApplicationChecks, showProductsSection, treatmentProducts, inventoryProducts, isCustomizing, numGreenhouses, totalBlocks, greenhouseAreaBreakdown, t, getPestName, handleLocationChange, handlePestChange, handleTreatmentChange, handleProductRateValueChange, handleProductRateUnitChange, handleRemoveProduct, handleAddProduct, caldoMissing, setIsCustomizing, notesOpen]);

  // Wizard navigation handlers
  const handleWizardStepChange = (step: number) => {
    setWizardStep(step);
  };

  /** Por qué no puede continuar este paso: un mensaje concreto, no el genérico. */
  const stepBlockReason = (stepId: string): string => {
    switch (stepId) {
      case "assigned-to": return t("planner.workerRequired");
      case "location": return t("planner.sectorRequired");
      case "pest-treatment":
        return caldoMissing && (formData.treatment_id && formData.treatment_id !== "none" || treatmentProducts.length > 0)
          ? t("planner.waterVolumeRequiredMsg")
          : t("planner.treatmentRequiredMsg");
      default: return t("planner.fillRequiredFields");
    }
  };

  const handleWizardContinue = () => {
    const visibleSteps = wizardSteps.filter(s => s.isVisible !== false);
    const currentStepObj = wizardSteps[wizardStep];

    // Validate required step
    if (currentStepObj?.isRequired && !currentStepObj.isCompleted()) {
      toast({ title: stepBlockReason(currentStepObj.id), variant: "destructive" });
      return;
    }
    
    // Find next visible step
    for (let i = wizardStep + 1; i < wizardSteps.length; i++) {
      if (wizardSteps[i].isVisible !== false) {
        setWizardStep(i);
        return;
      }
    }
  };

  const handleWizardBack = () => {
    // Find previous visible step
    for (let i = wizardStep - 1; i >= 0; i--) {
      if (wizardSteps[i].isVisible !== false) {
        // Nota: ya no se resetea manuallyAssigned al retroceder. Borrar la
        // elección manual al volver atrás la vaciaba delante del usuario, y
        // ahora además el selector está en Detalles: retroceder desde Ubicación
        // cae justo encima de él y se vería desaparecer el nombre elegido.
        setWizardStep(i);
        return;
      }
    }
  };

  // Submit button component (reused in both variants)
  const submitButton = (
    <Button 
      onClick={handleSubmitWithWarning} 
      className="w-full"
      disabled={!formData.title || loading}
    >
      {loading 
        ? t("common.saving") 
        : task 
          ? t("planner.updateTask") 
          : formData.greenhouse_selections.length > 1 
            ? t("planner.createTasks", { count: formData.greenhouse_selections.length })
            : t("planner.createTask")
      }
    </Button>
  );

  // ========== RENDER: el asistente de cuatro pasos ==========
  // Era el segundo de dos variantes (`variant="default"` pintaba el formulario
  // entero en una sola ventana). Los diez sitios que abren esta pantalla pasaban
  // ya `variant="wizard"`, así que la otra rama eran 470 líneas que no se
  // ejecutaban nunca — y una segunda hoja que había que mantener a la par con la
  // buena cada vez que se tocaba el cromo compartido.
  // Group existing wizardSteps into 4 visual macro stages.
  type MacroDef = { id: "details" | "location" | "treatment" | "schedule"; icon: React.ReactNode; titleKey: string; subtitleKey: string; stepIds: string[] };
  const macroDefs: MacroDef[] = [
    // "assigned-to" vuelve a Detalles, que es donde lo dice su propio subtítulo
    // («Título, tipo, prioridad y responsable») y donde se espera encontrarlo.
    // Estuvo tres días detrás de la ubicación para que el selector pudiera
    // ofrecer primero a los asignados a esos sectores; eso sigue funcionando
    // cuando la ubicación ya viene puesta (editar una tarea, crearla desde un
    // aviso), porque el agrupado mira `greenhouse_selections`, no el orden de
    // los pasos. En una tarea nueva y en blanco no hay ubicación todavía, así
    // que ahí el selector sale sin agrupar.
    { id: "details", icon: <FileText />, titleKey: "planner.macros.details", subtitleKey: "planner.macros.detailsSub", stepIds: ["title", "type-priority", "assigned-to"] },
    { id: "location", icon: <MapPin />, titleKey: "planner.macros.location", subtitleKey: "planner.macros.locationSub", stepIds: ["location"] },
    { id: "treatment", icon: <FlaskConical />, titleKey: "planner.macros.treatment", subtitleKey: "planner.macros.treatmentSub", stepIds: ["pest-treatment"] },
    { id: "schedule", icon: <CalendarIcon />, titleKey: "planner.macros.schedule", subtitleKey: "planner.macros.scheduleSub", stepIds: ["schedule", "notes"] },
  ];
  const visibleMacros = macroDefs
    .map(m => ({ ...m, subSteps: wizardSteps.filter(s => m.stepIds.includes(s.id) && s.isVisible !== false) }))
    .filter(m => m.subSteps.length > 0);

  const safeMacroIndex = Math.min(macroIndex, visibleMacros.length - 1);
  const activeMacro = visibleMacros[safeMacroIndex];
  const isLastMacro = safeMacroIndex >= visibleMacros.length - 1;

  const macroCanContinue = (m: typeof visibleMacros[number]) =>
    m.subSteps.every(s => !s.isRequired || s.isCompleted());

  const handleMacroContinue = () => {
    if (!macroCanContinue(activeMacro)) {
      const blocked = activeMacro.subSteps.find(s => s.isRequired && !s.isCompleted());
      toast({ title: stepBlockReason(blocked?.id || ""), variant: "destructive" });
      return;
    }
    const next = Math.min(safeMacroIndex + 1, visibleMacros.length - 1);
    setMacroIndex(next);
    setMaxMacroReached(prev => Math.max(prev, next));
  };
  const handleMacroBack = () => {
    const prev = Math.max(0, safeMacroIndex - 1);
    // Sin reseteo de manuallyAssigned: el selector está en la macro de
    // ubicación y una elección manual debe sobrevivir a la navegación.
    setMacroIndex(prev);
  };
  const handleMacroJump = (i: number) => {
    if (i <= maxMacroReached) setMacroIndex(i);
  };

  const stepperNodes: StepperNode[] = visibleMacros.map(m => ({
    id: m.id,
    icon: m.icon,
    title: t(m.titleKey, defaultMacroTitle(m.id)),
    subtitle: t(m.subtitleKey, defaultMacroSubtitle(m.id)),
    isRequired: m.subSteps.some(s => s.isRequired),
  }));

  // Sticky summary above footer for the Ubicación step
  const stickyAboveFooter =
    activeMacro?.id === "location" && formData.greenhouse_selections.length > 0 ? (
      <LocationSummaryBar
        greenhouseCount={formData.greenhouse_selections.length}
        blockCount={totalBlocks}
        totalAreaHa={totalAreaHa}
      />
    ) : null;

  // Body: stack the visible sub-step contents of the active macro
  const body = (
    <div className="space-y-5 py-2">
      {isScoutingLinked && safeMacroIndex === 0 && (
        <div className="flex items-start gap-2 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-sm text-blue-700 dark:text-blue-300">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{t("planner.scoutingLinkedInfo")}</span>
        </div>
      )}
      {prefilledData?.fromScouting && prefilledData?.location_chain && safeMacroIndex === 0 && (() => {
        const locIdx = visibleMacros.findIndex((m) => m.id === "location");
        const canJump = locIdx >= 0;
        return (
          <button
            type="button"
            disabled={!canJump}
            onClick={() => {
              if (locIdx < 0) return;
              setMacroIndex(locIdx);
              setMaxMacroReached((prev) => Math.max(prev, locIdx));
            }}
            className="w-full flex items-center gap-2 p-3 rounded-xl bg-primary/5 border border-[#b6dded] text-sm text-left hover:bg-primary/10 transition-colors disabled:cursor-default"
          >
            <MapPin className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
            <span className="font-medium text-foreground break-words flex-1">{prefilledData.location_chain}</span>
            {canJump && (
              <span className="text-xs font-semibold text-primary flex items-center gap-1 shrink-0">
                <Edit3 className="w-3.5 h-3.5" />
                {t("common.edit", "Editar")}
              </span>
            )}
          </button>
        );
      })()}
      {activeMacro?.subSteps.map(s => (
        <div key={s.id}>{s.content}</div>
      ))}
      {/* Resumen card on the last (Programación) macro */}
      {activeMacro?.id === "schedule" && (
        <div className="rounded-2xl border border-[#b6dded] bg-white shadow-sm p-4 space-y-2.5">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
            {t("planner.summary.resumen", "Resumen")}
          </div>
          <SummaryRow icon={<Layers className="w-4 h-4 text-black" />}
            text={formData.priority
              ? `${t(`planner.types.${formData.type}`)} · ${t(`planner.priorities.${formData.priority}`)}`
              : t(`planner.types.${formData.type}`)} />
          {formData.assigned_to && (
            <SummaryRow icon={<User className="w-4 h-4 text-black" />}
              text={companyWorkers.find(w => w.user_id === formData.assigned_to)?.full_name || ""} />
          )}
          {formData.greenhouse_selections.length > 0 && (
            <SummaryRow icon={<MapPin className="w-4 h-4 text-black" />}
              text={`${formData.greenhouse_selections.length} inv · ${totalBlocks} sec · ${totalAreaHa.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ha`} />
          )}
          {treatmentProducts.length > 0 && treatmentProducts[0] && (
            <SummaryRow icon={<FlaskConical className="w-4 h-4 text-black" />}
              text={`${treatmentProducts[0].product_name} · ${formatStockUtil(treatmentProducts[0].amount)} ${treatmentProducts[0].unit}`} />
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <TaskFormSheetShell
        open={open}
        onOpenChange={onOpenChange}
        title={task ? t("planner.editTask") : t("planner.createTask")}
        isDirty={!!taskFormIsDirty}
        nodes={stepperNodes}
        currentIndex={safeMacroIndex}
        reachedIndex={maxMacroReached}
        onJump={handleMacroJump}
        onBack={handleMacroBack}
        onContinue={handleMacroContinue}
        continueDisabled={!macroCanContinue(activeMacro)}
        isLastStep={isLastMacro}
        submitButton={submitButton}
        stickyAboveFooter={stickyAboveFooter}
        trailingMenu={appChoice.menu}
      >
        {body}
      </TaskFormSheetShell>

      {appChoice.sheets}

      <AlertDialog open={showMissingFieldsWarning} onOpenChange={setShowMissingFieldsWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {missingLocation && !missingWorker
                ? t("planner.noLocationWarningTitle")
                : t("planner.missingFieldsWarningTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {missingLocation && missingWorker
                ? (task ? t("planner.missingFieldsWarningBothEdit") : t("planner.missingFieldsWarningBoth"))
                : missingWorker
                  ? (task ? t("planner.missingFieldsWarningWorkerOnlyEdit") : t("planner.missingFieldsWarningWorkerOnly"))
                  : (task ? t("planner.noLocationWarningDescriptionEdit") : t("planner.noLocationWarningDescription"))}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setShowMissingFieldsWarning(false); handleSubmitCore(); }}>
              {t("common.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {doseConfirmDialog}
    </>
  );

}